import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { createPortal } from 'react-dom';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { CornerDownLeft, LineChart, BarChart3, Target, FileSearch, Users, Presentation, Cpu, Lightbulb, Layers, Trash2, Zap, X, Loader2, Compass } from 'lucide-react';
import WorkspaceView from './components/WorkspaceView';
import type { InsightFrame } from './components/DocumentViewer';
import BriefBuilderPanel, { BriefBuilderHandle } from './components/BriefBuilderPanel';
import DocumentUploadBar from './components/DocumentUploadBar';
import ExplorationPanel from './components/ExplorationPanel';
import AdminPanel from './AdminPanel';
import { apiService, isAbortError, StepStatusItem, SessionSummary, ChatResponse, API_BASE_URL, GenerateReportResponse, authErrorEmitter } from './services/api';
import type { MicroReport as ApiMicroReport, FrameCatalogResponse, ReportSectionsResponse, GenerationEstimateResponse, LaunchFlowLogRequest, DevReportWorkerJobResponse } from './services/api';
import { useCustomReportProgram } from './customReports';
import { applySuccessfulSessionName, getWorkspaceDisplayName, normalizeSessionName, shouldShowSessionNameModal } from './utils/sessionNaming';
import { getAgentLanguage } from './utils/agentLanguage';
import { SignInButton } from './components/SignInButton';
import { UserProfile } from './components/UserProfile';
import { UserProfileEditor } from './components/UserProfileEditor';
import { useAuth, AgentAccess } from './hooks/useAuth';
import './styles.css';
import astraBackground from './Astra_background.jpg';
import astraLogo from './ChatGPT Image Dec 11, 2025, 12_10_58 PM.png';
// Removed ReviewPanel; landing now directly triggers workspace setup

// For sync
// viewer wrapper (served by the backend at /api/developer_logs)
const DeveloperLogView: React.FC = () => {
  return (
    <iframe
      title="Developer Logs"
      src="/api/developer_logs"
      style={{ border: 'none', width: '100vw', height: '100vh' }}
    />
  );
};

type MicroReport = ApiMicroReport;

type LandingProgressStatus = 'pending' | 'running' | 'complete' | 'error';

type LandingStepId = 'frame_selector' | 'frame_generator' | 'framing_bundle';

type PendingGenerationMode = 'with_additional_frames' | 'without_additional_frames';

interface LandingProgressStep {
  id: LandingStepId;
  label: string;
  status: LandingProgressStatus;
  progress: number;
  detail?: string;
  timestamp?: string | null;
}

const LANDING_PROGRESS_TEMPLATE: Array<Omit<LandingProgressStep, 'status' | 'progress'>> = [
  { id: 'frame_selector', label: 'Studying current market trends' },
  { id: 'frame_generator', label: 'Preparing your workspace' },
  { id: 'framing_bundle', label: 'Frames ready' },
];

const LANDING_STATUS_PROGRESS: Record<LandingProgressStatus, number> = {
  pending: 0,
  running: 60,
  complete: 100,
  error: 100,
};

const buildLandingProgressSteps = (): LandingProgressStep[] =>
  LANDING_PROGRESS_TEMPLATE.map((step) => ({
    ...step,
    status: 'pending' as LandingProgressStatus,
    progress: 0,
    detail: '',
    timestamp: null,
  }));

// Removed LandingSummary; no longer parsing landing prompt

const SESSION_STORAGE_KEY = 'ca_session_id';
const WORKSPACE_ONBOARDING_HIDE_KEY = 'ca_workspace_onboarding_hidden';
const FIRST_TIME_TOUR_HIDE_KEY = 'ca_first_time_tour_hidden';

// Start the app at the pre-landing experience by default; only respect explicit paths
const getInitialView = (): 'prelanding' | 'landing' | 'workspace' => {
  if (typeof window === 'undefined') return 'prelanding';
  const path = window.location.pathname;
  if (path.startsWith('/workspace')) return 'workspace';
  if (path === '/landing') return 'landing';
  if (path === '/login') return 'prelanding';
  return 'prelanding';
};

const getStoredSessionId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(SESSION_STORAGE_KEY);
};

const PAGE_ONE_AGENT_BLOCKS = [
  { key: 'competitive', label: 'Competitive Analysis', desc: 'Compare competitors', icon: LineChart, enabled: true },
  { key: 'customer_research', label: 'Customer Analysis', desc: 'Segments, personas, and needs', icon: Users, enabled: true },
  { key: 'market-sizing', label: 'Market Sizing', desc: 'Estimate TAM/SAM/SOM', icon: FileSearch, enabled: false },
  { key: 'market_research', label: 'Market Research', desc: 'Analyze market trends', icon: BarChart3, enabled: true },
  { key: 'tech-analysis', label: 'Tech Analysis', desc: 'Technology Stack Analysis', icon: Cpu, enabled: false },
  { key: 'M&A-analysis', label: 'M&A Analysis', desc: 'Analyze M&A Strategies', icon: Target, enabled: false },
  { key: 'vertical-analysis', label: 'Vertical Analysis', desc: 'Deep dives by industry.', icon: Layers, enabled: false },
  { key: 'Pricing-analysis', label: 'Pricing Analysis', desc: 'Analyze Pricing Strategies', icon: Layers, enabled: false },
  { key: 'pitch-prep', label: 'Client Preparation', desc: 'Preparing for Clients.', icon: Presentation, enabled: false },
  { key: 'ideation', label: 'Ideation Panel', desc: 'Brainstorm with Experts', icon: Lightbulb, enabled: false },
];

// Popular agents shown as full cards (first 3)
const POPULAR_AGENT_KEYS = ['competitive', 'market_research', 'customer_research'];

const normalizeAgentKey = (agentKey: string): string => agentKey.trim().toLowerCase().replace(/-/g, '_');

// Map frontend agent keys to backend agent access keys
const mapAgentKeyToAccess = (agentKey: string): keyof AgentAccess | null => {
  const normalizedAgentKey = normalizeAgentKey(agentKey);
  const mapping: Record<string, keyof AgentAccess> = {
    'competitive': 'competitive',
    'customer_research': 'customer_research',
    'customer_analysis': 'customer_research',
    'market_research': 'market_research',
  };
  return mapping[normalizedAgentKey] || null;
};

// Check if agent is accessible based on user permissions
const isAgentAccessible = (agentKey: string, agentAccess: AgentAccess): boolean => {
  const accessKey = mapAgentKeyToAccess(agentKey);
  if (!accessKey) return false; // Unknown agents are not accessible
  return agentAccess[accessKey];
};

const LANDING_AGENT_ITEM = 'landing-agent';
// Toggle between the new "neo" landing layout and the legacy experience.
// Set to false to instantly revert to the previous landing layout without touching markup.
const USE_NEO_LANDING = true;

type LandingAgentConfig = (typeof PAGE_ONE_AGENT_BLOCKS)[number];

type LandingAgentDragItem = {
  agentKey: string;
};

const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M5.16 8.75H2.44V21h2.72V8.75ZM3.8 3a1.65 1.65 0 1 0 0 3.3 1.65 1.65 0 0 0 0-3.3Zm6.36 5.08H7.5V21h2.66v-6.09c0-1.6.48-2.7 2.02-2.7 1.89 0 1.9 1.77 1.9 2.8V21h2.66v-6.77c0-3.34-1.6-4.92-3.93-4.92-1.8 0-2.6.98-3.05 1.67h-.06Z"
    />
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M18.25 3.5H21l-5.5 6 6 8.5h-4.66l-3.65-5.1-4.18 5.1H3.25l6-7.35-5.7-7.15h4.8l3.36 4.78 3.58-4.78Z"
    />
  </svg>
);

const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M21.58 7.2a2.5 2.5 0 0 0-1.76-1.77C18.1 5 12 5 12 5s-6.1 0-7.82.43A2.5 2.5 0 0 0 2.42 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .42 4.8 2.5 2.5 0 0 0 1.76 1.77C5.9 19 12 19 12 19s6.1 0 7.82-.43a2.5 2.5 0 0 0 1.76-1.77c.28-1.58.42-3.17.42-4.8 0-1.63-.14-3.22-.42-4.8ZM10 15.02V8.98L15.2 12 10 15.02Z"
    />
  </svg>
);

const LandingFooter: React.FC = () => (
  <footer className="landing-footer" role="contentinfo">
    <div className="landing-footer__brand">
      <span>Lenz AI Innovation Studio, a Silicon Valley AI Company</span>
      <div className="landing-footer__brand-icons">
        <a
          href="https://youtube.com/@astra_by_lenz?si=TUqgVBdp1rgThl-K"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-footer__social-link"
          aria-label="YouTube"
        >
          <YoutubeIcon />
        </a>
        <a
          href="https://www.linkedin.com/company/lenzinnovation/"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-footer__social-link"
          aria-label="LinkedIn"
        >
          <LinkedinIcon />
        </a>
        <a
          href="https://astra.lenzinnovation.com?utm_source=astra_product&utm_medium=footer&utm_campaign=product_page"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-footer__social-link"
          aria-label="Astra Website"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
            />
          </svg>
        </a>
      </div>
    </div>
    <div className="landing-footer__links">
      <a href="/terms" className="landing-footer__link">Terms & Conditions</a>
      <span className="landing-footer__separator">|</span>
      <a href="/privacy" className="landing-footer__link">Privacy Policy</a>
    </div>
  </footer>
);

const COPILOT_STAGE_CONFIG = [
  { id: 'context', stageLabel: 'Stage 1', title: 'Building Context' },
  { id: 'competitors', stageLabel: 'Stage 2', title: 'Competitors Identified' },
  { id: 'output', stageLabel: 'Stage 3', title: 'Building Scope' },
] as const;

type CopilotStageId = (typeof COPILOT_STAGE_CONFIG)[number]['id'];
type CopilotStageState = 'idle' | CopilotStageId;

const COPILOT_STAGE_SEQUENCE: CopilotStageId[] = COPILOT_STAGE_CONFIG.map((stage) => stage.id);

const LandingAgentCard: React.FC<{
  agent: LandingAgentConfig;
  onReveal: (agentKey: string) => void;
  isDisabled?: boolean;
  isActive?: boolean;
  badgeLabel?: string;
}> = ({ agent, onReveal, isDisabled = false, isActive = false, badgeLabel }) => {
  const [{ isDragging }, dragRef] = useDrag<LandingAgentDragItem, void, { isDragging: boolean }>(
    () => ({
      type: LANDING_AGENT_ITEM,
      item: { agentKey: agent.key },
      canDrag: () => !isDisabled,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [agent.key, isDisabled]
  );
  const setDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      dragRef(node);
    },
    [dragRef]
  );
  const Icon = agent.icon;
  const handleActivate = useCallback(() => {
    if (isDisabled) return;
    onReveal(agent.key);
  }, [agent.key, onReveal, isDisabled]);
  return (
    <div
      ref={setDragRef}
      className={`landing-left-agent-card ${
        isDragging ? 'landing-left-agent-card--dragging' : ''
      } ${isDisabled ? 'landing-left-agent-card--disabled' : ''} ${
        isActive ? 'landing-left-agent-card--active' : ''
      }`}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleActivate();
        }
      }}
      aria-disabled={isDisabled}
      aria-label={`${agent.label} agent card`}
      title={badgeLabel || agent.label}
    >
      <div className="landing-left-agent-icon-wrap">
        <Icon className="landing-left-agent-icon" />
      </div>
      <div className="landing-left-agent-title">{agent.label}</div>
      <div className="landing-left-agent-desc">{agent.desc}</div>
      {badgeLabel && (
        <div className="landing-left-agent-coming-soon">{badgeLabel}</div>
      )}
    </div>
  );
};

const LandingAgentIconCard: React.FC<{
  agent: LandingAgentConfig;
  onReveal: (agentKey: string) => void;
  isDisabled?: boolean;
  isActive?: boolean;
  badgeLabel?: string;
}> = ({ agent, onReveal, isDisabled = false, isActive = false, badgeLabel }) => {
  const [{ isDragging }, dragRef] = useDrag<LandingAgentDragItem, void, { isDragging: boolean }>(
    () => ({
      type: LANDING_AGENT_ITEM,
      item: { agentKey: agent.key },
      canDrag: () => !isDisabled,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [agent.key, isDisabled]
  );
  const setDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      dragRef(node);
    },
    [dragRef]
  );
  const Icon = agent.icon;
  const handleActivate = useCallback(() => {
    if (isDisabled) return;
    onReveal(agent.key);
  }, [agent.key, onReveal, isDisabled]);

  return (
    <div
      ref={setDragRef}
      className={`landing-left-agent-icon-card ${
        isDragging ? 'landing-left-agent-icon-card--dragging' : ''
      } ${isDisabled ? 'landing-left-agent-icon-card--disabled' : ''} ${
        isActive ? 'landing-left-agent-icon-card--active' : ''
      }`}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleActivate();
        }
      }}
      aria-disabled={isDisabled}
      aria-label={`${agent.label} agent`}
    >
      <Icon className="landing-left-agent-icon-card__icon" />
      <span className="landing-left-agent-icon-card__tooltip">
        {badgeLabel ? (
          <>
            <span className="landing-left-agent-icon-card__tooltip-line">{agent.label}</span>
            <span className="landing-left-agent-icon-card__tooltip-line landing-left-agent-icon-card__tooltip-badge">{badgeLabel}</span>
          </>
        ) : (
          agent.label.split(' ').map((part, idx, arr) => (
            <span key={`${part}-${idx}`} className="landing-left-agent-icon-card__tooltip-line">
              {part}
              {idx < arr.length - 1 && <br />}
            </span>
          ))
        )}
      </span>
    </div>
  );
};

interface LandingViewProps {
  landingRevealAgent: string | null;
  agentId?: string | null;
  onRevealAgent: (agentKey: string | null) => void;
  showLandingProgress: boolean;
  showBriefBuilder: boolean;
  landingInput: string;
  onLandingInputChange: (value: string) => void;
  onLandingSeedSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isLandingParsing: boolean;
  landingError: string | null;
  landingProgressSteps: LandingProgressStep[];
  landingProgressPercent: number;
  landingProgressStatusText: string;
  onLaunchWorkspaceFromBrief: (args: { briefId?: string | null; finalPrompt: string }) => Promise<void>;
  briefBuilderRef: React.RefObject<BriefBuilderHandle | null>;
  briefSidebarHostRef?: React.RefObject<HTMLDivElement | null>;
  draftSessionId: string | null;
  ensureDraftSession: () => Promise<string>;
  fetchSessions: () => void | Promise<void>;
  sessionsLoading: boolean;
  sessionsError: string | null;
  availableSessions: SessionSummary[];
  deletingSessionId: string | null;
  onResumeSession: (sessionId: string) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
  onCreateTestWorkspace?: () => Promise<void>;
  showDevSmokeButton?: boolean;
  devSmokeRunning?: boolean;
  devSmokeStatusText?: string | null;
  onRunDevSmokeTest?: () => Promise<void>;
  onResetLandingPage: () => void;
  isMarketResearch: boolean;
  agentAccess: AgentAccess;
  isSignedIn: boolean;
  profile: { name: string | null; company: string | null; role: string | null };
  refreshProfile: () => Promise<void>;
  CreditDisplay: React.FC;
}

const LandingView: React.FC<LandingViewProps> = ({
  landingRevealAgent,
  agentId,
  onRevealAgent,
  showLandingProgress,
  showBriefBuilder,
  landingInput,
  onLandingInputChange,
  onLandingSeedSubmit,
  isLandingParsing,
  landingError,
  landingProgressSteps,
  landingProgressPercent,
  landingProgressStatusText,
  onLaunchWorkspaceFromBrief,
  briefBuilderRef,
  briefSidebarHostRef,
  draftSessionId,
  ensureDraftSession,
  fetchSessions,
  sessionsLoading,
  sessionsError,
  availableSessions,
  deletingSessionId,
  onResumeSession,
  onDeleteSession,
  onCreateTestWorkspace,
  showDevSmokeButton,
  devSmokeRunning,
  devSmokeStatusText,
  onRunDevSmokeTest,
  onResetLandingPage,
  isMarketResearch,
  agentAccess,
  isSignedIn,
  profile,
  refreshProfile,
  CreditDisplay,
}) => {

  const internalSidebarHostRef = useRef<HTMLDivElement | null>(null);
  const sidebarHostRef = briefSidebarHostRef ?? internalSidebarHostRef;
  const landingInputRef = useRef<HTMLTextAreaElement | null>(null);
  // While an uploaded document is being ingested, pause the chat input so the
  // conversation always sees the document once it's ready.
  const [briefDocsBusy, setBriefDocsBusy] = useState(false);
  const shouldShowLandingSidePanel = showLandingProgress || !showBriefBuilder;
  const landingLayoutClassName = ['landing-layout', shouldShowLandingSidePanel ? 'landing-layout--with-rail' : '']
    .filter(Boolean)
    .join(' ');
  const landingPrimaryClassName = ['landing-primary', shouldShowLandingSidePanel ? 'landing-primary--with-rail' : '']
    .filter(Boolean)
    .join(' ');
  const [{ isOver: isOverLandingDrop }, landingDropRef] = useDrop<LandingAgentDragItem, void, { isOver: boolean }>(
    () => ({
      accept: LANDING_AGENT_ITEM,
      drop: (item: LandingAgentDragItem) => {
        onRevealAgent(item.agentKey);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [onRevealAgent]
  );
  const setLandingDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      landingDropRef(node);
    },
    [landingDropRef]
  );
  const landingContainerClassNameList = [
    'landing-container',
    landingRevealAgent ? 'landing-container--revealed' : '',
    !landingRevealAgent ? 'landing-container--idle' : '',
    !landingRevealAgent && isOverLandingDrop ? 'landing-container--drop-active' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const revealedAgentConfig = PAGE_ONE_AGENT_BLOCKS.find((entry) => entry.key === landingRevealAgent);
  const revealedAgentLabel = revealedAgentConfig?.label || 'Competitive Analysis';
  const agentLanguage = getAgentLanguage(agentId || landingRevealAgent);
  const [showExploration, setShowExploration] = useState(false);
  const handleResetLandingSelection = useCallback(() => {
    // Reset all landing page state to initial state (like first login)
    setShowExploration(false);
    onResetLandingPage();
    // Also reset copilot stage to idle
    setCopilotStage('idle');
  }, [onResetLandingPage]);
  const [copilotStage, setCopilotStage] = useState<CopilotStageState>('idle');

  const resolveStageVisualState = useCallback(
    (stageId: CopilotStageId): 'pending' | 'active' | 'complete' => {
      if (copilotStage === 'idle') return 'pending';
      const activeIndex = COPILOT_STAGE_SEQUENCE.indexOf(copilotStage);
      const stageIndex = COPILOT_STAGE_SEQUENCE.indexOf(stageId);
      if (stageIndex < activeIndex) return 'complete';
      if (stageIndex === activeIndex) return 'active';
      return 'pending';
    },
    [copilotStage]
  );
  const shouldShowCopilotStageBar = showBriefBuilder && copilotStage !== 'idle';

  const advanceCopilotStage = useCallback((nextStage: CopilotStageId) => {
    setCopilotStage((prev) => {
      const prevIdx = prev === 'idle' ? -1 : COPILOT_STAGE_SEQUENCE.indexOf(prev);
      const nextIdx = COPILOT_STAGE_SEQUENCE.indexOf(nextStage);
      if (prev === 'idle' || prevIdx < nextIdx) {
        return nextStage;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (!showBriefBuilder) {
      setCopilotStage('idle');
    }
  }, [showBriefBuilder]);

  const handleConversationStart = useCallback(() => {
    advanceCopilotStage('context');
  }, [advanceCopilotStage]);

  const handleCompetitorStart = useCallback(() => {
    advanceCopilotStage('competitors');
  }, [advanceCopilotStage]);

  const handleOutputStart = useCallback(() => {
    advanceCopilotStage('output');
  }, [advanceCopilotStage]);

  const handleBriefLaunch = useCallback(
    async (options: { briefId?: string | null; finalPrompt: string }) => {
      await onLaunchWorkspaceFromBrief(options);
    },
    [onLaunchWorkspaceFromBrief]
  );

  const resizeLandingInput = useCallback(() => {
    const el = landingInputRef.current;
    if (!el) return;
    const minHeight = 40;
    const maxHeight = 240;
    el.style.height = 'auto';
    const nextHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeLandingInput();
  }, [landingInput, resizeLandingInput]);

  const handleLandingKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (isLandingParsing) return;
      event.currentTarget.form?.requestSubmit();
    }
  };

  const landingShellClassName = [
    'landing-shell w-screen bg-gradient-to-br from-indigo-950 via-slate-950 to-slate-900 text-white px-6 py-10 overflow-y-auto',
    USE_NEO_LANDING ? 'landing-shell--orbit' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderLegacyLanding = () => (
    <div className="landing-shell w-screen bg-gradient-to-br from-indigo-950 via-slate-950 to-slate-900 text-white px-6 py-10 overflow-y-auto">
      <div className="landing-shell__body">
      <div className="landing-duo">
        <section className="landing-left-panel" aria-label="Agent shortcuts">
          <div className="landing-left-panel__header landing-left-panel__header--with-logo">
            <img src={astraLogo} alt="astra logo" className="landing-left-logo" />
            <div className="landing-left-panel__title-block">
            <h2>astra Strategy Agents</h2>
            <p>Drag and drop to load the agent.</p>
            </div>
          </div>
          <div className="landing-left-agent-section" aria-label="Agent shortcuts">
            {/* Popular agents - full cards */}
            <div className="landing-left-popular-agents">
              {POPULAR_AGENT_KEYS.map((agentKey) => {
                const agent = PAGE_ONE_AGENT_BLOCKS.find((item) => item.key === agentKey);
                if (!agent) return null;
                const isActiveAgent = landingRevealAgent === agent.key;
                const hasAccess = isAgentAccessible(agent.key, agentAccess);
                const isComingSoon = !agent.enabled;
                const isNoAccess = !hasAccess;
                const isLockedBySelection = Boolean(landingRevealAgent && !isActiveAgent);
                const isDisabledAgent = isComingSoon || isNoAccess || isLockedBySelection;
                const badgeLabel = isComingSoon ? 'Coming Soon' : (isNoAccess ? 'No Access' : undefined);
                return (
                  <LandingAgentCard
                    key={agent.key}
                    agent={agent}
                    onReveal={onRevealAgent}
                    isDisabled={isDisabledAgent}
                    isActive={isActiveAgent}
                    badgeLabel={badgeLabel}
                  />
                );
              })}
            </div>
            {/* Other agents - icon grid */}
            <div className="landing-left-agent-icon-grid">
              {PAGE_ONE_AGENT_BLOCKS.filter((agent) => !POPULAR_AGENT_KEYS.includes(agent.key)).map((agent) => {
                const isActiveAgent = landingRevealAgent === agent.key;
                const hasAccess = isAgentAccessible(agent.key, agentAccess);
                const isComingSoon = !agent.enabled;
                const isNoAccess = !hasAccess;
                const isLockedBySelection = Boolean(landingRevealAgent && !isActiveAgent);
                const isDisabledAgent = isComingSoon || isNoAccess || isLockedBySelection;
                const badgeLabel = isComingSoon ? 'Coming Soon' : (isNoAccess ? 'No Access' : undefined);
                return (
                  <LandingAgentIconCard
                    key={agent.key}
                    agent={agent}
                    onReveal={onRevealAgent}
                    isDisabled={isDisabledAgent}
                    isActive={isActiveAgent}
                    badgeLabel={badgeLabel}
                  />
                );
              })}
            </div>
          </div>
          {landingRevealAgent && (
            <div className="landing-left-panel-footer">
              <button type="button" className="landing-left-panel-action" onClick={handleResetLandingSelection}>
                Deploy another agent
              </button>
            </div>
          )}
          {showDevSmokeButton && (
            <div className="landing-left-panel-footer" style={{ marginTop: '10px' }}>
              <button
                type="button"
                className="landing-left-panel-action"
                onClick={() => {
                  void onRunDevSmokeTest?.();
                }}
                disabled={Boolean(devSmokeRunning)}
              >
                {devSmokeRunning ? 'Running report worker smoke test...' : 'Run report worker smoke test'}
              </button>
              {devSmokeStatusText ? (
                <p style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '8px', lineHeight: 1.4 }}>
                  {devSmokeStatusText}
                </p>
              ) : null}
            </div>
          )}
        </section>
        <div ref={setLandingDropRef} className={landingContainerClassNameList}>
          {!landingRevealAgent && (
            <div className="landing-drop-cover">
              <div className="landing-drop-cover__inner">
                <p className="landing-drop-cover__eyebrow">Agent Loading Zone</p>
                <h3 className="landing-drop-cover__body">Drag any agent card from the left into this panel to load the agent.</h3>
              </div>
              {isSignedIn && (
                <div className="landing-profile-section">
                  <UserProfileEditor
                    profile={profile}
                    onUpdate={refreshProfile}
                  />
                </div>
              )}
            </div>
          )}
          <div
            className={`landing-container-body ${landingRevealAgent ? 'landing-container-body--visible' : ''}`}
            aria-hidden={!landingRevealAgent}
          >
            <div className={landingLayoutClassName}>
              <div className={landingPrimaryClassName}>
                <div className="landing-primary__prompt-region">
                  <div className="landing-primary__prompt-header mb-4 text-left">
                    <div className="landing-primary__prompt-header-main">
                      <h1 className="text-3xl font-semibold text-white">
                        {landingRevealAgent ? `${revealedAgentLabel} Agent` : 'Competitive Analysis Agent'}
                      </h1>
                      <p className="text-indigo-200 mt-3 text-sm">
                        What would you like to analyze? Since i am purpose built for the above specific task, please keep the nature of the analysis aligned to the task.
                      </p>
                      {shouldShowCopilotStageBar && (
                        <div className="copilot-stage-bar" role="status" aria-live="polite">
                          {COPILOT_STAGE_CONFIG.map((stage) => {
                            const status = resolveStageVisualState(stage.id);
                            const title =
                              stage.id === 'competitors' ? agentLanguage.stage2Title : stage.title;
                            return (
                              <div
                                key={stage.id}
                                className={`copilot-stage-bar__segment copilot-stage-bar__segment--${status}`}
                              >
                                <div className="copilot-stage-bar__stage-label">{stage.stageLabel}</div>
                                <div className="copilot-stage-bar__stage-title">{title}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {showBriefBuilder && (
                    <div className="landing-brief-builder-embed">
                      <BriefBuilderPanel
                        ref={briefBuilderRef}
                        onLaunchWorkspace={handleBriefLaunch}
                        isLaunching={isLandingParsing}
                        hideInput
                        agentId={agentId}
                        sessionId={draftSessionId}
                        ensureSessionId={ensureDraftSession}
                        onConversationStart={handleConversationStart}
                        onCompetitorStart={handleCompetitorStart}
                        onOutputStart={handleOutputStart}
                        onDocumentsBusyChange={setBriefDocsBusy}
                      />
                    </div>
                  )}

                  {!showBriefBuilder && landingRevealAgent && (
                    <DocumentUploadBar
                      sessionId={draftSessionId}
                      ensureSessionId={ensureDraftSession}
                      onBusyChange={setBriefDocsBusy}
                    />
                  )}
                  <div className="landing-primary__prompt-form">
                    <form onSubmit={onLandingSeedSubmit}>
                      <div className="relative">
                        <textarea
                          ref={landingInputRef}
                          rows={2}
                          value={landingInput}
                          onChange={(e) => onLandingInputChange(e.target.value)}
                          onKeyDown={handleLandingKeyDown}
                          placeholder={
                            briefDocsBusy
                              ? 'Reading your document… chat will resume in a moment'
                              : 'e.g. I am building a new product. I want to run a competitive analysis of key competitors so that I can identify gaps'
                          }
                          className="landing-input"
                          disabled={isLandingParsing || briefDocsBusy}
                        />
                        <CornerDownLeft
                          className={`landing-input-icon ${isLandingParsing ? 'landing-input-icon--disabled' : ''}`}
                        />
                      </div>
                    </form>
                  </div>
                </div>
                {!showLandingProgress && isLandingParsing && (
                  <div className="landing-resume-note mt-4">
                    Research and Analysis Workspace is being set up...
                  </div>
                )}
                {landingError && (
                  <div className="landing-error mt-4">
                    {landingError}
                  </div>
                )}
              </div>
            </div>
          </div>
          {shouldShowLandingSidePanel && (
            <aside className="landing-side-panel">
              {isSignedIn && (
                <div className="landing-profile-card">
                  <UserProfileEditor
                    profile={profile}
                    onUpdate={refreshProfile}
                  />
                </div>
              )}
              {showLandingProgress && (
                <div className="landing-progress-card">
                  <div className="landing-progress-card__header">
                    <div>
                      <div className="landing-progress-eyebrow">Research workspace</div>
                      <div className="landing-progress-title">Hang tight—preparing your research workspace (est ~2 mins)</div>
                    </div>
                  </div>
                  <div className="landing-progress-steps">
                    <div className="landing-progress-step">
                      <div className="landing-progress-step-top">
                        <span className="landing-progress-label">Workspace progress</span>
                        <span className="landing-progress-status landing-progress-status--running">
                          {landingProgressPercent}%
                        </span>
                      </div>
                      <div className="landing-progress-bar">
                        <div
                          className="landing-progress-bar-fill landing-progress-bar-fill--running"
                          style={{ width: `${Math.min(Math.max(landingProgressPercent, 0), 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!showBriefBuilder && (
                <div>
                  <div className="landing-resume-card">
                    <div className="landing-resume-header">
                      <div>
                        <div className="landing-resume-eyebrow">Resume</div>
                        <h2 className="landing-resume-title">Previous workspaces</h2>
                      </div>
                      <button type="button" onClick={() => fetchSessions()} className="landing-resume-refresh">
                        Refresh
                      </button>
                    </div>
                    {sessionsLoading && (
                      <div className="landing-resume-note">
                        Loading workspaces…
                      </div>
                    )}
                    {sessionsError && !sessionsLoading && (
                      <div className="landing-error mt-0 mb-4">
                        {sessionsError}
                      </div>
                    )}
                    {!sessionsLoading && availableSessions.length === 0 && !sessionsError && (
                      <div className="landing-resume-note">
                        No prior workspaces yet. Start a new one above to see it here.
                      </div>
                    )}
                    {availableSessions.length > 0 && (
                      <div className="landing-resume-grid">
                        {availableSessions
                          .filter((session) => normalizeSessionName(session.session_name))
                          .map((session) => {
                      const displayName = getWorkspaceDisplayName(session.session_name);
                      const isDeleting = deletingSessionId === session.session_id;
                          return (
                            <div key={session.session_id} className="landing-resume-row">
                              <button
                                className="landing-resume-item landing-resume-item-full"
                                type="button"
                                onClick={() => {
                                  void onResumeSession(session.session_id);
                                }}
                                disabled={isLandingParsing || isDeleting}
                              >
                                <div className="landing-resume-item-title">{displayName}</div>
                                <div className="landing-resume-item-date">
                                  {isDeleting ? 'Deleting…' : formatDate(session.created_at)}
                                </div>
                              </button>
                              <button
                                type="button"
                                className="landing-resume-delete"
                                aria-label="Delete workspace"
                                disabled={isDeleting}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void onDeleteSession(session.session_id);
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>
      </div>
    </div>
    <LandingFooter />
    </div>
  );

  if (!USE_NEO_LANDING) {
    return renderLegacyLanding();
  }

  return (
    <div className={landingShellClassName}>
      <div className="orbit-grid">
        <aside className="orbit-rail" aria-label="Agent shortcuts">
          <div className="orbit-rail__header">
            <div className="orbit-rail__brand">
              <span className="orbit-rail__brand-dot" />
              <div>
                <div className="orbit-rail__brand-title">astra Agents</div>
                <div className="orbit-rail__brand-sub">Drag to deploy</div>
              </div>
            </div>
            <button
              type="button"
              className="orbit-rail__reset"
              onClick={handleResetLandingSelection}
              disabled={!landingRevealAgent && !showExploration}
            >
              Reset
            </button>
          </div>
          <div className="orbit-rail__list" aria-label="Agents list">
            <div className="landing-left-agent-section">
              {/* Popular agents - full cards */}
              <div className="landing-left-popular-agents">
                {POPULAR_AGENT_KEYS.map((agentKey) => {
                  const agent = PAGE_ONE_AGENT_BLOCKS.find((item) => item.key === agentKey);
                  if (!agent) return null;
                  const isActiveAgent = landingRevealAgent === agent.key;
                  const hasAccess = isAgentAccessible(agent.key, agentAccess);
                  const isComingSoon = !agent.enabled;
                  const isNoAccess = !hasAccess;
                  const isLockedBySelection = Boolean(landingRevealAgent && !isActiveAgent) || showExploration;
                  const isDisabledAgent = isComingSoon || isNoAccess || isLockedBySelection;
                  const badgeLabel = isComingSoon ? 'Coming Soon' : (isNoAccess ? 'No Access' : undefined);
                  return (
                    <LandingAgentCard
                      key={agent.key}
                      agent={agent}
                      onReveal={onRevealAgent}
                      isDisabled={isDisabledAgent}
                      isActive={isActiveAgent}
                      badgeLabel={badgeLabel}
                    />
                  );
                })}
              </div>
              {/* Other agents - icon grid */}
              <div className="landing-left-agent-icon-grid">
                {PAGE_ONE_AGENT_BLOCKS.filter((agent) => !POPULAR_AGENT_KEYS.includes(agent.key)).map((agent) => {
                  const isActiveAgent = landingRevealAgent === agent.key;
                  const hasAccess = isAgentAccessible(agent.key, agentAccess);
                  const isComingSoon = !agent.enabled;
                  const isNoAccess = !hasAccess;
                  const isLockedBySelection = Boolean(landingRevealAgent && !isActiveAgent) || showExploration;
                  const isDisabledAgent = isComingSoon || isNoAccess || isLockedBySelection;
                  const badgeLabel = isComingSoon ? 'Coming Soon' : (isNoAccess ? 'No Access' : undefined);
                  return (
                    <LandingAgentIconCard
                      key={agent.key}
                      agent={agent}
                      onReveal={onRevealAgent}
                      isDisabled={isDisabledAgent}
                      isActive={isActiveAgent}
                      badgeLabel={badgeLabel}
                    />
                  );
                })}
              </div>
              <div className="exploration-entry">
                <button
                  type="button"
                  className={`exploration-entry__btn ${showExploration ? 'exploration-entry__btn--active' : ''}`}
                  onClick={() => setShowExploration(true)}
                  disabled={Boolean(landingRevealAgent)}
                >
                  <span className="exploration-entry__icon">
                    <Compass size={18} strokeWidth={2} />
                  </span>
                  <span>
                    <span className="exploration-entry__title">Astra Exploration Mode</span>
                    <span className="exploration-entry__desc">Chat or search with any AI model</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className="orbit-rail__footer">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
              <UserProfile />
              <CreditDisplay />
            </div>
          </div>
        </aside>

        <main
          ref={setLandingDropRef}
          className={`orbit-stage ${landingRevealAgent ? 'orbit-stage--active' : ''} ${isOverLandingDrop ? 'orbit-stage--drop' : ''}`}
          aria-label="Agent loading zone"
        >
          {!landingRevealAgent && !showExploration && (
            <div className="orbit-stage__empty">
              <div className="orbit-stage__empty-main">
                <p className="orbit-stage__eyebrow">Agent Loading Zone</p>
                <h2 className="orbit-stage__title">Drop an agent card to load it</h2>
              </div>
              {isSignedIn && (
                <div className="landing-profile-section orbit-stage__profile">
                  <UserProfileEditor
                    profile={profile}
                    onUpdate={refreshProfile}
                  />
                </div>
              )}
            </div>
          )}

          {showExploration && !landingRevealAgent && (
            <div className="orbit-stage__body orbit-stage__body--visible orbit-stage__body--exploration">
              <ExplorationPanel
                renderSidebar={(sidebar) =>
                  sidebarHostRef.current ? createPortal(sidebar, sidebarHostRef.current) : sidebar
                }
              />
            </div>
          )}

          <div
            className={`orbit-stage__body ${landingRevealAgent ? 'orbit-stage__body--visible' : 'orbit-stage__body--hidden'} ${showBriefBuilder ? 'orbit-stage__body--brief' : ''}`}
            aria-hidden={!landingRevealAgent}
          >
            <div className="landing-primary__prompt-region">
              <div className="landing-primary__prompt-header mb-4 text-left">
                <div className="landing-primary__prompt-header-main">
                  <h1 className="text-3xl font-semibold text-white">
                    {landingRevealAgent ? `${revealedAgentLabel} Agent` : 'Competitive Analysis Agent'}
                  </h1>
                  <p className="text-indigo-200 mt-3 text-sm">
                    What would you like to analyze? I am built to perform deep {agentLanguage.analysisPhrase}.
                  </p>
                  {shouldShowCopilotStageBar && (
                    <div className="copilot-stage-bar" role="status" aria-live="polite">
                      {COPILOT_STAGE_CONFIG.map((stage) => {
                        const status = resolveStageVisualState(stage.id);
                        const title =
                          stage.id === 'competitors' ? agentLanguage.stage2Title : stage.title;
                        return (
                          <div
                            key={stage.id}
                            className={`copilot-stage-bar__segment copilot-stage-bar__segment--${status}`}
                          >
                            <div className="copilot-stage-bar__stage-label">{stage.stageLabel}</div>
                            <div className="copilot-stage-bar__stage-title">{title}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {showBriefBuilder && (
                <div className="orbit-brief">
                  <BriefBuilderPanel
                    ref={briefBuilderRef}
                    onLaunchWorkspace={handleBriefLaunch}
                    isLaunching={isLandingParsing}
                    hideInput
                    agentId={agentId}
                    sessionId={draftSessionId}
                    ensureSessionId={ensureDraftSession}
                    onConversationStart={handleConversationStart}
                    onCompetitorStart={handleCompetitorStart}
                    onOutputStart={handleOutputStart}
                    onDocumentsBusyChange={setBriefDocsBusy}
                          renderSidebar={(sidebar) =>
                            sidebarHostRef.current ? createPortal(sidebar, sidebarHostRef.current) : sidebar
                          }
                  />
                </div>
              )}

              {!showBriefBuilder && landingRevealAgent && (
                <DocumentUploadBar
                  sessionId={draftSessionId}
                  ensureSessionId={ensureDraftSession}
                  onBusyChange={setBriefDocsBusy}
                />
              )}
              <div className="landing-primary__prompt-form">
                <form onSubmit={onLandingSeedSubmit}>
                  <div className="relative">
                    <textarea
                      ref={landingInputRef}
                      rows={2}
                      value={landingInput}
                      onChange={(e) => onLandingInputChange(e.target.value)}
                      onKeyDown={handleLandingKeyDown}
                      placeholder={
                        briefDocsBusy
                          ? 'Reading your document… chat will resume in a moment'
                          : 'e.g. I am building a new product. I want to run a competitive analysis of key competitors so that I can identify gaps'
                      }
                      className="landing-input"
                      disabled={isLandingParsing || briefDocsBusy}
                    />
                    <CornerDownLeft
                      className={`landing-input-icon ${isLandingParsing ? 'landing-input-icon--disabled' : ''}`}
                    />
                  </div>
                </form>
              </div>
            </div>
            {!showLandingProgress && isLandingParsing && (
              <div className="landing-resume-note mt-4">
                Research and Analysis Workspace is being set up...
              </div>
            )}
            {landingError && (
              <div className="landing-error mt-4">
                {landingError}
              </div>
            )}
          </div>
        </main>

        <aside className="orbit-status" aria-label="Status and previous workspaces">
          <div ref={sidebarHostRef} className="orbit-structured-host" aria-label="Structured panels host" />
          {!showBriefBuilder && !!landingRevealAgent && (
            <div className="landing-resume-card">
              <div className="landing-resume-header">
                <div>
                  <div className="landing-resume-eyebrow">Resume</div>
                  <h2 className="landing-resume-title">Previous workspaces</h2>
                </div>
                <button type="button" onClick={() => fetchSessions()} className="landing-resume-refresh">
                  Refresh
                </button>
              </div>
              {sessionsLoading && (
                <div className="landing-resume-note">
                  Loading workspaces…
                </div>
              )}
              {sessionsError && !sessionsLoading && (
                <div className="landing-error mt-0 mb-4">
                  {sessionsError}
                </div>
              )}
              {!sessionsLoading && availableSessions.length === 0 && !sessionsError && (
                <div className="landing-resume-note">
                  No prior workspaces yet. Start a new one above to see it here.
                </div>
              )}
              {availableSessions.length > 0 && (
                <div className="landing-resume-grid">
                  {availableSessions
                    .filter((session) => normalizeSessionName(session.session_name))
                    .map((session) => {
                      const displayName = getWorkspaceDisplayName(session.session_name);
                    const isDeleting = deletingSessionId === session.session_id;
                    return (
                      <div key={session.session_id} className="landing-resume-row">
                        <button
                          className="landing-resume-item landing-resume-item-full"
                          type="button"
                          onClick={() => {
                            void onResumeSession(session.session_id);
                          }}
                          disabled={isLandingParsing || isDeleting}
                        >
                          <div className="landing-resume-item-title">{displayName}</div>
                          <div className="landing-resume-item-date">
                            {isDeleting ? 'Deleting…' : formatDate(session.created_at)}
                          </div>
                        </button>
                        <button
                          type="button"
                          className="landing-resume-delete"
                          aria-label="Delete workspace"
                          disabled={isDeleting}
                          onClick={(event) => {
                            event.stopPropagation();
                            void onDeleteSession(session.session_id);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {showLandingProgress && (
            <div className="landing-progress-card">
              <div className="landing-progress-card__header">
                <div>
                  <div className="landing-progress-eyebrow">Research workspace</div>
                  <div className="landing-progress-title">Hang tight—preparing your research workspace (est ~2 mins)</div>
                </div>
              </div>
              <div className="landing-progress-steps">
                <div className="landing-progress-step">
                  <div className="landing-progress-step-top">
                    <span className="landing-progress-label">Workspace progress</span>
                    <span className="landing-progress-status landing-progress-status--running">
                      {landingProgressPercent}%
                    </span>
                  </div>
                  <div className="landing-progress-bar">
                    <div
                      className="landing-progress-bar-fill landing-progress-bar-fill--running"
                      style={{ width: `${Math.min(Math.max(landingProgressPercent, 0), 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>
    </div>
    <LandingFooter />
    </div>
  );
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const isCustomReportDebugEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('ca_custom_report_debug') === '1';

const isLaunchProgressDebugEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('ca_debug_launch_progress') === '1';

const LegalPageContainer: React.FC<{ children: React.ReactNode; title: string }> = ({ children, title }) => (
  <div className="legal-page-container">
    <div className="legal-page-content">
      <h1 className="legal-page-title">{title}</h1>
      {children}
    </div>
    <LandingFooter />
  </div>
);

const TermsAndConditions: React.FC = () => (
  <LegalPageContainer title="Terms & Conditions">
    <div className="legal-section">
      <p className="legal-effective-date">Effective Date: Mar 18th, 2026</p>
      
      <h2>1. Acceptance of Terms</h2>
      <p>By accessing or using astra (the "Platform" or "Service"), operated by Lenz LLC ("Lenz," "Company," "we," "us," or "our"), you agree to be legally bound by these Terms & Conditions ("Terms").</p>
      <p>If you are using astra on behalf of a company, organization, or other legal entity, you represent and warrant that you have full legal authority to bind that entity to these Terms, and "you" refers to both you and that entity.</p>
      <p>If you do not agree to these Terms, you must not access or use the Service.</p>
      <p>If your organization has entered into a separate master services agreement, order form, or enterprise agreement with Lenz, that agreement will control to the extent of any conflict with these Terms.</p>

      <h2>2. Nature of the Platform and Beta Status</h2>
      <p>astra is a beta-stage, AI-powered strategy and innovation platform. It is provided for early access, evaluation, testing, and limited-use purposes only and is not production-grade.</p>
      <p>astra may:</p>
      <ul>
        <li>generate strategic recommendations, analyses, and business plans,</li>
        <li>conduct internet-based and third-party research,</li>
        <li>process user-provided company, market, and contextual information,</li>
        <li>produce outputs through automated AI systems without human validation.</li>
      </ul>
      <p>astra may be incomplete, unstable, unavailable, or contain errors, defects, inaccuracies, or security vulnerabilities. Features may change significantly or be discontinued at any time without notice.</p>
      <p>astra is not intended for mission-critical, production, or high-risk use. You use astra entirely at your own risk.</p>

      <h2>3. Eligibility</h2>
      <p>You must be at least eighteen (18) years old or the age of majority in your jurisdiction, whichever is higher, to use the Service.</p>
      <p>By using astra, you represent and warrant that:</p>
      <ul>
        <li>you meet the eligibility requirements,</li>
        <li>you have not previously been suspended or removed from the Service, and</li>
        <li>your registration and use of the Service complies with all applicable laws and regulations.</li>
      </ul>

      <h2>4. Account Registration and Security</h2>
      <p>To access certain features of the Service, you may be required to register for an account and provide accurate, complete, and current information.</p>
      <p>You agree to:</p>
      <ul>
        <li>maintain the accuracy of your account information,</li>
        <li>keep your login credentials confidential,</li>
        <li>accept responsibility for all activities that occur under your account.</li>
      </ul>
      <p>You must promptly notify us of any unauthorized use of your account or any other breach of security.</p>
      <p>You may not share your account or make it available to unauthorized third parties unless expressly permitted.</p>

      <h2>5. Input, Output, and Content</h2>
      <p>You may provide data, prompts, documents, company information, and other materials to the Service ("Input"), and the Service may generate analyses, recommendations, reports, business plans, financial projections, and other materials ("Output"). Input and Output together constitute "Content."</p>
      <p>You are solely responsible for your Content, including ensuring that:</p>
      <ul>
        <li>you have all necessary rights, licenses, permissions, and authorizations to provide the Input,</li>
        <li>your Content does not violate any applicable law, regulation, or third-party rights,</li>
        <li>your use of Output complies with applicable law and these Terms.</li>
      </ul>

      <h2>6. Ownership of Content</h2>
      <p>As between you and Lenz, and to the extent permitted by law:</p>
      <ul>
        <li>you retain all ownership rights in your Input, and</li>
        <li>you may use Output generated for you by the Service.</li>
      </ul>
      <p>However, you acknowledge that:</p>
      <ul>
        <li>Output may not be unique,</li>
        <li>other users may receive similar or identical outputs,</li>
        <li>Lenz does not guarantee that Output is original, non-infringing, or protectable under intellectual property laws.</li>
      </ul>

      <h2>7. Use of Content; No Training Without Opt-In</h2>
      <p>We may use Content to:</p>
      <ul>
        <li>provide, operate, and maintain the Service,</li>
        <li>improve system performance and reliability,</li>
        <li>detect and prevent abuse, fraud, and security incidents,</li>
        <li>comply with legal obligations and enforce these Terms.</li>
      </ul>
      <p>We do not use customer Content to train general-purpose AI models by default. Any such use, if introduced, will be subject to explicit user consent or applicable contractual agreements.</p>

      <h2>8. Data Processing, System Usage, and Data Minimization</h2>
      <p>To operate, secure, and improve the Service, Lenz processes data generated through your use of astra, including system interactions, usage patterns, operational metrics, and related information.</p>
      
      <h3>8.1 Permitted Use of Service Data</h3>
      <p>Except as expressly limited below, you agree that Lenz may process data associated with your use of the Service for purposes including:</p>
      <ul>
        <li>operating and delivering the Service,</li>
        <li>improving system performance, reliability, and functionality,</li>
        <li>monitoring usage, billing, and cost allocation,</li>
        <li>security, fraud detection, and abuse prevention,</li>
        <li>analytics, product development, and feature optimization,</li>
        <li>compliance with legal obligations and enforcement of these Terms.</li>
      </ul>

      <h3>8.2 Content Handling and Limited Retention</h3>
      <p>The Service may temporarily process and store user-provided prompts, inputs, and AI-generated responses as necessary to:</p>
      <ul>
        <li>deliver outputs,</li>
        <li>maintain system performance,</li>
        <li>debug and improve reliability.</li>
      </ul>
      <p>Such content may be retained for a limited duration and handled with additional safeguards due to its potentially sensitive nature.</p>

      <h3>8.3 Data Minimization Commitment</h3>
      <p>Lenz is committed to minimizing persistent storage of sensitive business information.</p>
      <p>The Service is designed not to retain certain categories of data beyond active use, including:</p>
      <ul>
        <li>generated reports,</li>
        <li>structured outputs such as business plans or deliverables,</li>
        <li>uploaded documents (unless explicitly required for processing).</li>
      </ul>
      <p>Users are responsible for retaining their own copies of such materials.</p>

      <h3>8.4 Aggregated and De-Identified Data</h3>
      <p>Lenz may use data derived from use of the Service in aggregated and/or de-identified form for:</p>
      <ul>
        <li>analytics,</li>
        <li>system improvement,</li>
        <li>benchmarking,</li>
        <li>product development.</li>
      </ul>
      <p>Such data will not identify you or your organization.</p>

      <h3>8.5 Model Training and Data Use</h3>
      <p>Lenz does not use customer Content to train general-purpose AI models by default.</p>
      <p>If such use is introduced in the future, it will be subject to explicit user consent or applicable contractual agreements.</p>
      <p>The Service may rely on third-party AI providers to process inputs and generate outputs. Such providers may process data in accordance with their own terms and policies.</p>

      <h2>9. AI Limitations and Accuracy</h2>
      <p>You acknowledge that artificial intelligence and machine learning technologies have inherent limitations.</p>
      <p>Output may:</p>
      <ul>
        <li>contain errors, omissions, or hallucinations,</li>
        <li>be incomplete, outdated, biased, repetitive, or formulaic,</li>
        <li>fail to capture nuance, context, or real-world constraints,</li>
        <li>appear persuasive or authoritative despite being incorrect.</li>
      </ul>
      <p>You agree that:</p>
      <ul>
        <li>you are solely responsible for evaluating all Output,</li>
        <li>you bear all risks associated with reliance on Output,</li>
        <li>you will not rely on Output as a sole source of truth,</li>
        <li>you will apply appropriate human judgment, verification, and validation before using or sharing Output.</li>
      </ul>

      <h2>10. No Professional Advice; No Reliance</h2>
      <p>The Service does not provide legal, financial, accounting, tax, investment, regulatory, or other professional advice.</p>
      <p>No Output should be construed as such advice.</p>
      <p>You agree that:</p>
      <ul>
        <li>Output is provided for informational and exploratory purposes only,</li>
        <li>you will not rely on Output as a substitute for professional advice,</li>
        <li>you are solely responsible for all decisions and actions taken based on Output.</li>
      </ul>

      <h2>11. Strategy and Business Decision Disclaimer</h2>
      <p>The Service may generate strategic recommendations, market analyses, MVP concepts, business plans, financial projections, and operational guidance.</p>
      <p>You acknowledge that:</p>
      <ul>
        <li>such outputs are inherently uncertain and speculative,</li>
        <li>actual outcomes depend on numerous external factors beyond the control of the Service,</li>
        <li>Lenz makes no representations or guarantees regarding outcomes.</li>
      </ul>
      <p>Lenz shall not be responsible or liable for any business decisions, strategies, investments, or outcomes derived from the Service.</p>

      <h2>12. Confidential Information and Sensitive Data</h2>
      <p>You may choose to provide confidential, proprietary, or commercially sensitive information to the Service.</p>
      <p>You represent and warrant that:</p>
      <ul>
        <li>you have all necessary rights and authority to provide such information.</li>
      </ul>
      <p>Unless expressly agreed in writing by Lenz, you agree not to submit:</p>
      <ul>
        <li>protected health information subject to HIPAA,</li>
        <li>payment card data subject to PCI-DSS,</li>
        <li>regulated financial data subject to GLBA,</li>
        <li>export-controlled or classified information,</li>
        <li>or any data subject to heightened regulatory requirements.</li>
      </ul>
      <p>We implement reasonable safeguards but do not guarantee absolute confidentiality or security.</p>

      <h2>13. Third-Party Services and Data</h2>
      <p>The Service may incorporate or rely on third-party services, software, data sources, APIs, or AI models.</p>
      <p>We are not responsible for:</p>
      <ul>
        <li>third-party services or their performance,</li>
        <li>accuracy or reliability of third-party data,</li>
        <li>third-party terms or policies.</li>
      </ul>

      <h2>14. Beta Services</h2>
      <p>astra is beta software.</p>
      <p>It is provided on an early-access, evaluation, testing, and non-production basis only. astra:</p>
      <ul>
        <li>may be incomplete, unstable, or unavailable,</li>
        <li>may contain bugs, errors, inaccuracies, or security vulnerabilities,</li>
        <li>may change significantly or be discontinued at any time.</li>
      </ul>
      <p>astra is provided "as is" and "as available" without warranties, support, maintenance, or service guarantees of any kind.</p>
      <p>Lenz shall have no liability whatsoever arising out of or relating to astra's beta status, including any errors, inaccuracies, system failures, data loss, or security incidents.</p>

      <h2>15. Autonomous Actions and System Behavior</h2>
      <p>If the Service enables automated workflows, recommendations, or system actions without full manual review, you acknowledge and agree that:</p>
      <ul>
        <li>such features may produce unintended, incorrect, or harmful outcomes,</li>
        <li>you assume all risks associated with their use,</li>
        <li>you are solely responsible for implementing appropriate safeguards, validation, monitoring, and controls.</li>
      </ul>

      <h2>16. Acceptable Use Restrictions</h2>
      <p>Except to the extent prohibited by law, you may not:</p>
      <ul>
        <li>reverse engineer, decompile, or attempt to extract underlying models or systems,</li>
        <li>copy, modify, distribute, or create derivative works of the Service,</li>
        <li>use Output to build or train competing systems,</li>
        <li>scrape or extract data from the Service at scale,</li>
        <li>bypass safeguards or security controls,</li>
        <li>use the Service for unlawful or harmful purposes,</li>
        <li>upload malicious or harmful content,</li>
        <li>violate any third-party rights.</li>
      </ul>

      <h2>17. Benchmarking Restrictions</h2>
      <p>You may not publish or disclose performance or benchmarking results of the Service in a misleading, incomplete, or non-reproducible manner.</p>

      <h2>18. Feedback</h2>
      <p>If you provide feedback, suggestions, or ideas, you grant Lenz a perpetual, irrevocable, royalty-free right to use such feedback without restriction or compensation.</p>

      <h2>19. Fees, Billing, and Subscriptions</h2>
      <p>If you purchase paid features:</p>
      <ul>
        <li>fees will be disclosed prior to purchase,</li>
        <li>subscriptions may renew automatically,</li>
        <li>fees are non-refundable except where required by law,</li>
        <li>you are responsible for applicable taxes.</li>
      </ul>
      <p>We may change pricing with notice. Continued use constitutes acceptance.</p>
      <p>Failure to pay may result in suspension or termination.</p>

      <h2>20. No Support Obligation</h2>
      <p>Lenz is under no obligation to provide support, maintenance, or service levels unless expressly agreed in writing.</p>

      <h2>21. Termination and Suspension</h2>
      <p>You may stop using the Service at any time.</p>
      <p>We may suspend or terminate access:</p>
      <ul>
        <li>for violation of these Terms,</li>
        <li>for legal or security reasons,</li>
        <li>for non-payment,</li>
        <li>for inactivity.</li>
      </ul>
      <p>We may modify or discontinue the Service at any time.</p>
      <p>Upon termination, we may delete Content without liability.</p>

      <h2>22. Disclaimer of Warranties</h2>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE."</p>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, LENZ DISCLAIMS ALL WARRANTIES, INCLUDING:</p>
      <ul>
        <li>accuracy,</li>
        <li>reliability,</li>
        <li>fitness for a particular purpose,</li>
        <li>non-infringement.</li>
      </ul>
      <p>WE DO NOT GUARANTEE:</p>
      <ul>
        <li>uninterrupted operation,</li>
        <li>error-free performance,</li>
        <li>secure or loss-free data.</li>
      </ul>

      <h2>23. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
      <p>LENZ SHALL NOT BE LIABLE FOR:</p>
      <ul>
        <li>INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES,</li>
        <li>LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITY,</li>
        <li>DECISIONS OR ACTIONS BASED ON OUTPUT.</li>
      </ul>
      <p>TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF:</p>
      <ul>
        <li>THE AMOUNT PAID IN THE SIX (6) MONTHS PRIOR TO THE CLAIM, OR</li>
        <li>ONE HUNDRED DOLLARS ($100).</li>
      </ul>

      <h2>24. Indemnification</h2>
      <p>You agree to indemnify and hold harmless Lenz from any claims arising from:</p>
      <ul>
        <li>your use of the Service,</li>
        <li>your Content,</li>
        <li>your violation of these Terms,</li>
        <li>infringement of third-party rights.</li>
      </ul>

      <h2>25. Dispute Resolution</h2>
      <p>All disputes shall be resolved through binding arbitration on an individual basis.</p>
      <p>You waive:</p>
      <ul>
        <li>class actions,</li>
        <li>jury trials.</li>
      </ul>
      <p>California law governs these Terms.</p>

      <h2>26. Copyright Complaints</h2>
      <p>To report infringement, contact: astra.lenzinnovation@gmail.com</p>

      <h2>27. Electronic Communications</h2>
      <p>You consent to receive communications electronically.</p>

      <h2>28. Export and Trade Compliance</h2>
      <p>You must comply with applicable export and sanctions laws.</p>

      <h2>29. Assignment</h2>
      <p>You may not assign these Terms without consent.</p>
      <p>We may assign them without restriction.</p>

      <h2>30. Changes to Terms</h2>
      <p>We may update these Terms. Continued use constitutes acceptance.</p>

      <h2>31. Entire Agreement</h2>
      <p>These Terms constitute the entire agreement between you and Lenz.</p>
    </div>
  </LegalPageContainer>
);

const PrivacyPolicy: React.FC = () => (
  <LegalPageContainer title="Privacy Policy">
    <div className="legal-section">
      <p className="legal-effective-date">Effective Date: March 18th 2026</p>
      <p>Lenz LLC ("Lenz," "we," "us," or "our") operates astra, an AI-powered strategy and innovation platform (the "Service").</p>
      <p>This Privacy Policy explains how we collect, use, process, store, and disclose information when you use astra.</p>
      <p>By using the Service, you agree to this Privacy Policy.</p>

      <h2>1. Scope of This Policy</h2>
      <p>This Privacy Policy applies to:</p>
      <ul>
        <li>your use of astra,</li>
        <li>any associated applications, APIs, or interfaces,</li>
        <li>communications with us related to the Service.</li>
      </ul>
      <p>This Policy does not apply to third-party services that may be integrated into or accessed through astra.</p>

      <h2>2. Information We Collect</h2>
      <p>We collect information in three categories:</p>

      <h3>2.1 Information You Provide</h3>
      <p>You may provide information directly when using the Service, including:</p>
      <ul>
        <li>account information (name, email, organization),</li>
        <li>prompts, inputs, queries, and instructions,</li>
        <li>business data, documents, or context you choose to share,</li>
        <li>communications with us (support requests, feedback).</li>
      </ul>

      <h3>2.2 Automatically Collected Information</h3>
      <p>When you use astra, we may collect:</p>
      <ul>
        <li>usage data (features used, interactions, session activity),</li>
        <li>technical data (IP address, browser type, device information),</li>
        <li>performance and diagnostic data (logs, error reports, latency metrics),</li>
        <li>billing and subscription data (if applicable).</li>
      </ul>

      <h3>2.3 Derived and Aggregated Data</h3>
      <p>We may generate:</p>
      <ul>
        <li>analytics and usage insights,</li>
        <li>system performance metrics,</li>
        <li>aggregated or de-identified datasets.</li>
      </ul>
      <p>These do not identify you or your organization.</p>

      <h2>3. How We Use Information</h2>
      <p>We use information to:</p>
      <ul>
        <li>provide, operate, and maintain the Service,</li>
        <li>generate outputs and respond to user inputs,</li>
        <li>improve system performance, reliability, and functionality,</li>
        <li>monitor usage, billing, and cost allocation,</li>
        <li>detect, prevent, and respond to security threats or abuse,</li>
        <li>develop new features and capabilities,</li>
        <li>comply with legal obligations and enforce our Terms.</li>
      </ul>

      <h2>4. Content Handling and Retention</h2>
      <p>astra processes user inputs and generated outputs to deliver the Service.</p>
      <ul>
        <li>Content may be temporarily stored for system operation, debugging, and performance improvement.</li>
        <li>We aim to limit persistent storage of sensitive business data.</li>
        <li>Certain outputs, such as generated reports or structured deliverables, are not retained by default beyond active processing.</li>
      </ul>
      <p>You are responsible for saving any outputs you wish to retain.</p>

      <h2>5. Model Training and AI Systems</h2>
      <ul>
        <li>We do not use customer Content to train general-purpose AI models by default.</li>
        <li>If this changes, it will be subject to explicit user consent or contractual agreement.</li>
      </ul>
      <p>astra may rely on third-party AI providers to process inputs and generate outputs. Those providers may process data in accordance with their own terms and privacy policies.</p>

      <h2>6. Data Sharing and Disclosure</h2>
      <p>We do not sell your personal information.</p>
      <p>We may share information:</p>

      <h3>6.1 Service Providers</h3>
      <p>With vendors and infrastructure providers who support:</p>
      <ul>
        <li>hosting, storage, and compute,</li>
        <li>payment processing,</li>
        <li>analytics and monitoring,</li>
        <li>AI model processing.</li>
      </ul>

      <h3>6.2 Legal and Compliance</h3>
      <p>When required to:</p>
      <ul>
        <li>comply with law, regulation, or legal process,</li>
        <li>respond to lawful requests from authorities,</li>
        <li>enforce our Terms,</li>
        <li>protect rights, safety, or property.</li>
      </ul>

      <h3>6.3 Business Transfers</h3>
      <p>In connection with:</p>
      <ul>
        <li>mergers, acquisitions, financing, or asset sales.</li>
      </ul>

      <h3>6.4 Aggregated / De-Identified Data</h3>
      <p>We may share aggregated or de-identified data that does not identify you.</p>

      <h2>7. Data Retention</h2>
      <p>We retain information:</p>
      <ul>
        <li>for as long as necessary to provide the Service,</li>
        <li>to comply with legal obligations,</li>
        <li>to resolve disputes and enforce agreements,</li>
        <li>for legitimate business purposes such as analytics and security.</li>
      </ul>
      <p>We aim to minimize retention of sensitive business data where feasible.</p>

      <h2>8. Data Security</h2>
      <p>We implement reasonable administrative, technical, and organizational safeguards designed to protect information.</p>
      <p>However:</p>
      <ul>
        <li>no system is completely secure,</li>
        <li>we cannot guarantee absolute security of data.</li>
      </ul>

      <h2>9. Your Responsibilities</h2>
      <p>You are responsible for:</p>
      <ul>
        <li>ensuring you have rights to provide any data you submit,</li>
        <li>not submitting data that is subject to heightened regulatory protections unless permitted,</li>
        <li>maintaining copies of important outputs and materials.</li>
      </ul>

      <h2>10. Your Rights and Choices</h2>
      <p>Depending on your jurisdiction, you may have rights to:</p>
      <ul>
        <li>access your personal data,</li>
        <li>correct inaccurate data,</li>
        <li>request deletion,</li>
        <li>restrict or object to processing,</li>
        <li>request data portability.</li>
      </ul>
      <p>To exercise these rights, contact: astra.lenzinnovation@gmail.com</p>
      <p>We may need to verify your identity before fulfilling requests.</p>

      <h2>11. International Data Transfers</h2>
      <p>Your information may be processed in the United States or other jurisdictions where data protection laws may differ.</p>
      <p>By using the Service, you consent to such transfers.</p>

      <h2>12. Children's Privacy</h2>
      <p>astra is not intended for use by individuals under 18. We do not knowingly collect personal data from children.</p>

      <h2>13. Third-Party Services</h2>
      <p>astra may integrate or rely on third-party services.</p>
      <p>We are not responsible for the privacy practices of third parties. You should review their policies independently.</p>

      <h2>14. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time.</p>
      <p>We will notify you of material changes by:</p>
      <ul>
        <li>posting an updated version,</li>
        <li>updating the effective date.</li>
      </ul>
      <p>Continued use of the Service constitutes acceptance.</p>

      <h2>15. Contact Us</h2>
      <p>If you have questions about this Privacy Policy, contact:</p>
      <p>Lenz LLC<br />astra.lenzinnovation@gmail.com</p>
    </div>
  </LegalPageContainer>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: isAuthLoaded, credits, agentAccess, profile, refreshProfile, refreshCredits } = useAuth();
  const { signOut } = useClerkAuth();
  const [activeView, setActiveView] = useState<'prelanding' | 'landing' | 'workspace'>(getInitialView);
  const requestedViewRef = useRef<'prelanding' | 'landing' | 'workspace' | null>(null);

  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/developer' || location.pathname === '/terms' || location.pathname === '/privacy') {
      return;
    }
    
    const targetPath =
      activeView === 'workspace' ? '/workspace' : activeView === 'landing' ? '/landing' : '/';

    if (location.pathname === targetPath) {
      if (requestedViewRef.current === activeView) {
        requestedViewRef.current = null;
      }
      return;
    }

    requestedViewRef.current = activeView;
    navigate(targetPath, { replace: true });
  }, [activeView, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/developer' || location.pathname === '/terms' || location.pathname === '/privacy') {
      return;
    }
    
    const pendingView = requestedViewRef.current;
    if (pendingView) {
      const expectedPath =
        pendingView === 'workspace' ? '/workspace' : pendingView === 'landing' ? '/landing' : '/';
      if (location.pathname === expectedPath) {
        requestedViewRef.current = null;
      } else {
        return;
      }
    }

    if (location.pathname === '/workspace' && activeView !== 'workspace') {
      setActiveView('workspace');
    } else if (location.pathname === '/landing' && activeView !== 'landing') {
      setActiveView('landing');
    } else if ((location.pathname === '/' || location.pathname === '/login') && activeView !== 'prelanding') {
      setActiveView('prelanding');
    }
  }, [location.pathname, activeView]);

  useEffect(() => {
    if (!isAuthLoaded || isSignedIn) {
      return;
    }
    if (location.pathname === '/admin' || location.pathname === '/developer' || location.pathname === '/terms' || location.pathname === '/privacy') {
      return;
    }
    if (location.pathname === '/landing' || location.pathname === '/workspace') {
      setActiveView('prelanding');
      navigate('/login', { replace: true });
    }
  }, [isAuthLoaded, isSignedIn, location.pathname, navigate]);

  // Listen for persistent auth failures and handle gracefully
  useEffect(() => {
    const unsubscribe = authErrorEmitter.on((error) => {
      console.error('[Auth] Persistent 401 detected:', error);
      // Log for monitoring but don't log out user - the retry mechanism should handle it
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const scrollableRoutes = ['/terms', '/privacy'];
    if (scrollableRoutes.includes(location.pathname)) {
      document.body.classList.add('body--scrollable');
    } else {
      document.body.classList.remove('body--scrollable');
    }
    return () => document.body.classList.remove('body--scrollable');
  }, [location.pathname]);

  const [isLandingParsing, setIsLandingParsing] = useState(false);
  const [landingInput, setLandingInput] = useState('');
  const [landingError, setLandingError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<MicroReport | null>(null);
  const [shouldAutoSelectLatest, setShouldAutoSelectLatest] = useState<boolean>(true);
  const [categories, setCategories] = useState<string[]>(["Ungrouped"]);
  const [scratchpadItems, setScratchpadItems] = useState<any[]>([]);
  const [microreports, setMicroreports] = useState<MicroReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditSupportError, setCreditSupportError] = useState<string | null>(null);
  const [sessionAccessError, setSessionAccessError] = useState<string | null>(null);
  const [isAnalysisGenerationBusy, setIsAnalysisGenerationBusy] = useState(false);
  const [isChatBusy, setIsChatBusy] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(280); // default reduced for wider document viewer
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [selectedFrameNumbers, setSelectedFrameNumbers] = useState<number[]>([]);
  const [completedFrameNumbers, setCompletedFrameNumbers] = useState<number[]>([]);
  const [microreportsVisible, setMicroreportsVisible] = useState<boolean>(false);
  const [pendingFrameSelection, setPendingFrameSelection] = useState<InsightFrame[] | null>(null);
  const [pendingGenerationMode, setPendingGenerationMode] = useState<PendingGenerationMode | null>(null);
  const [showCreditConfirmationModal, setShowCreditConfirmationModal] = useState<boolean>(false);
  // Chat is now fully decoupled from research flow; no external chat events
  const framesNoticeRef = useRef<string | null>(null);
  const [frameGroups, setFrameGroups] = useState<FrameCatalogResponse | null>(null);
  const [sessionId, setSessionIdState] = useState<string | null>(getStoredSessionId);
  const updateSessionId = useCallback((nextId: string | null) => {
    setSessionIdState(nextId);
    apiService.setSessionId(nextId);
    if (typeof window !== 'undefined') {
      if (nextId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, nextId);
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
    if (!nextId) {
      setWorkspaceAccessRequested(false);
    }
  }, []);

  useEffect(() => {
    apiService.setSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    scopeAutoSelectedRef.current = false;
  }, [sessionId]);

  const [stepStatuses, setStepStatuses] = useState<StepStatusItem[]>([]);
  const [, setSelectedMicroreportIds] = useState<string[]>([]);
  const [stepStage, setStepStage] = useState<string | null>(null);
  const [finalRegenerating, setFinalRegenerating] = useState<boolean>(false);
  const [reportSections, setReportSections] = useState<ReportSectionsResponse | null>(null);
  const [landingRevealAgent, setLandingRevealAgent] = useState<string | null>(null);
  const [availableSessions, setAvailableSessions] = useState<SessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const pollAuthBackoffUntilRef = useRef<number>(0);
  const stepStatusInFlightRef = useRef<boolean>(false);
  const microreportsInFlightRef = useRef<boolean>(false);
  const frameGroupsInFlightRef = useRef<boolean>(false);
  const reportSectionsInFlightRef = useRef<boolean>(false);
  const stepStatusLastAtRef = useRef<number>(0);
  const reportSectionsLastAtRef = useRef<number>(0);
  // True only while first-time launch is waiting for backend readiness signals.
  const launchReadinessGateActiveRef = useRef<boolean>(false);
  const reportRefreshInFlightRef = useRef<Set<string>>(new Set());
  const reportRefreshLastAtRef = useRef<Map<string, number>>(new Map());

  const currentAgentId = useMemo(() => {
    if (landingRevealAgent) return normalizeAgentKey(landingRevealAgent);
    if (sessionId && availableSessions.length > 0) {
      const match = availableSessions.find((s) => s.session_id === sessionId);
      if (match?.agent_id) return normalizeAgentKey(match.agent_id);
    }
    return null;
  }, [landingRevealAgent, sessionId, availableSessions]);

  const isMarketResearch = currentAgentId === 'market_research';

  const filteredSessions = useMemo(() => {
    const agentFilter = normalizeAgentKey(currentAgentId || 'competitive');
    return availableSessions.filter((s) => normalizeAgentKey(s.agent_id || 'competitive') === agentFilter);
  }, [availableSessions, currentAgentId]);

  useEffect(() => {
    if (landingRevealAgent) {
      // eslint-disable-next-line no-console
      console.log('[App] Agent selected from landing', { agentId: landingRevealAgent });
    }
  }, [landingRevealAgent]);

  const handleRevealLandingAgent = useCallback((agentKey: string | null) => {
    if (!agentKey) {
      setLandingRevealAgent(null);
      return;
    }
    setLandingRevealAgent(normalizeAgentKey(agentKey));
  }, []);

  const scopeReport = useMemo<MicroReport | null>(() => {
    if (!reportSections) return null;
    const { scope, context, competitors, trend_categories } = reportSections;
    const normalizedCompetitors = competitors && competitors.trim() ? competitors.trim() : '';
    const normalizedTrends = trend_categories && trend_categories.trim() ? trend_categories.trim() : '';
    const normalizeContext = (value?: string) =>
      (value || '')
        .replace(/^\s*#{1,6}\s*/gm, '')
        .trim();

    const formatContext = (value: string) => {
      const lines = value
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const formatted: string[] = [];
      lines.forEach((line) => {
        const headingMatch = line.match(/^(\d+\.?\s+[^:]+:?)(\s*(.*))?$/);
        if (headingMatch) {
          const title = headingMatch[1].replace(/[:\s]+$/, '').trim();
          const remainder = (headingMatch[3] || '').trim();
          formatted.push(remainder ? `**${title}** — ${remainder}` : `**${title}**`);
        } else {
          formatted.push(line);
        }
      });
      // Add a blank line between paragraphs for readability
      return formatted.join('\n\n');
    };

    const parts: string[] = [];
    const contextBlock = context && context.trim() ? formatContext(normalizeContext(context)) : '';
    if (contextBlock) {
      parts.push(`## Context\n${contextBlock}`);
    }
    if (isMarketResearch) {
      const trendContent = normalizedTrends || normalizedCompetitors;
      if (trendContent) {
        parts.push(`## TREND CATEGORIES\n\n<div class="scope-competitors">\n\n${trendContent}\n\n</div>`);
      }
    } else if (normalizedCompetitors) {
      const heading = getAgentLanguage(currentAgentId).scopeHeading;
      parts.push(`## ${heading}\n\n<div class="scope-competitors">\n\n${normalizedCompetitors}\n\n</div>`);
    }
    if (scope && scope.trim()) {
      parts.push(`## Scope\n${scope.trim()}`);
    }
    if (parts.length === 0) return null;
    const timestamp = new Date().toISOString();
    return {
      id: 'scope-overview',
      title: 'Scope',
      date: timestamp,
      content: `# Scope\n\n${parts.join('\n\n')}`,
      type: 'scope',
    };
  }, [reportSections, isMarketResearch, currentAgentId]);
  const [augmentationWorkerRunning, setAugmentationWorkerRunning] = useState<boolean>(false);
  const [augmentationStatusMessage, setAugmentationStatusMessage] = useState<string | null>(null);
  const [analysisQueuePending, setAnalysisQueuePending] = useState<boolean>(false);
  const [isKillingGeneration, setIsKillingGeneration] = useState<boolean>(false);
  const [journeyStarted, setJourneyStarted] = useState<boolean>(false);
  const [journeyUnlocked, setJourneyUnlocked] = useState<boolean>(false);
  const [landingProgressSteps, setLandingProgressSteps] = useState<LandingProgressStep[]>(() => buildLandingProgressSteps());
  const [showLandingProgress, setShowLandingProgress] = useState<boolean>(false);
  const [landingProgressPercent, setLandingProgressPercent] = useState<number>(0);
  const [landingProgressStatusText, setLandingProgressStatusText] = useState<string>('working (0%)');
  const landingStatusPollerRef = useRef<number | null>(null);
  const landingStatusInFlightRef = useRef<boolean>(false);
  const latestLandingStatusUpdatedAtRef = useRef<number>(0);
  const landingStatusTelemetrySignatureRef = useRef<string>('');
  const scopeSectionsTelemetrySignatureRef = useRef<string>('');
  const scopeSelectionTelemetrySignatureRef = useRef<string>('');
  const landingProgressAnimRef = useRef<number | null>(null);
  const landingProgressPercentRef = useRef<number>(0);
  const landingProgressTargetRef = useRef<number>(0);
  const landingBackendSignalRef = useRef<number>(0);
  const landingProgressPhaseRef = useRef<'idle' | 'backend' | 'reports' | 'frames' | 'done'>('idle');
  const landingLastLoggedPercentRef = useRef<number>(0);
  // Canvas categories tracked via ref to avoid re-render during selection
  const canvasCategoriesRef = useRef<string[]>([]);
  const [sessionName, setSessionName] = useState<string>('');
  const [sessionNameInput, setSessionNameInput] = useState<string>('');
  const [showSessionNameModal, setShowSessionNameModal] = useState<boolean>(false);
  const [savingSessionName, setSavingSessionName] = useState<boolean>(false);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [showBriefBuilder, setShowBriefBuilder] = useState(false);
  const briefBuilderRef = useRef<BriefBuilderHandle | null>(null);
  const [pendingBriefPrompt, setPendingBriefPrompt] = useState<string | null>(null);
  const [draftSessionId, setDraftSessionId] = useState<string | null>(null);
  const [workspaceBriefId, setWorkspaceBriefId] = useState<string | null>(null);
  const draftSessionPromiseRef = useRef<Promise<string> | null>(null);
  const isNewWorkspaceRef = useRef<boolean>(false);
  const [showWorkspaceOnboarding, setShowWorkspaceOnboarding] = useState<boolean>(false);
  const [workspaceOnboardingOptOut, setWorkspaceOnboardingOptOut] = useState<boolean>(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState<boolean>(false);
  const [showFirstTimeTour, setShowFirstTimeTour] = useState<boolean>(false);
  const [firstTimeTourOptOut, setFirstTimeTourOptOut] = useState<boolean>(false);
  const [tourStep, setTourStep] = useState<'intro' | 'microreports' | 'documentviewer' | 'rightpanel' | 'scratchpad' | null>(null);
  const [isClosingWorkspace, setIsClosingWorkspace] = useState<boolean>(false);
  const [generationEstimate, setGenerationEstimate] = useState<GenerationEstimateResponse | null>(null);
  const [isEstimatingGenerationCredits, setIsEstimatingGenerationCredits] = useState<boolean>(false);
  const [generationEstimateError, setGenerationEstimateError] = useState<string | null>(null);
  const [devSmokeWorkerJob, setDevSmokeWorkerJob] = useState<DevReportWorkerJobResponse | null>(null);
  const [devSmokeWorkerError, setDevSmokeWorkerError] = useState<string | null>(null);
  const [isStartingDevSmokeWorker, setIsStartingDevSmokeWorker] = useState<boolean>(false);
  const pendingSessionNameModalRef = useRef<boolean>(false);
  const sessionNameModalDismissedForSessionRef = useRef<string | null>(null);
  const generationEstimateRequestIdRef = useRef<number>(0);
  const researchAutoStartRequestedRef = useRef<boolean>(false);
  const queueReloadStorageKey = 'astra_analysis_queue_reload';
  const queueReloadAfterMs = 180000;
  const queueReloadIntervalMs = 15000;
  const reportGenerationPollIntervalMs = 10000;
  const stepStatusMinIntervalMs = 10000;
  const reportSectionsMinIntervalMs = 10000;
  const isAnalysisGenerationStage = useCallback((stage: string) => {
    return stage.startsWith('step_research_') || stage === 'final_report_running';
  }, []);

  const emitLaunchFlowEvent = useCallback((payload: LaunchFlowLogRequest) => {
    const eventPayload: LaunchFlowLogRequest = {
      ...payload,
      timestamp: payload.timestamp || new Date().toISOString(),
    };
    void apiService.logLaunchFlow(eventPayload);
  }, []);

  const logLaunchProgressDebug = useCallback((event: string, payload?: Record<string, unknown>) => {
    if (!isLaunchProgressDebugEnabled()) return;
    // eslint-disable-next-line no-console
    console.debug('[launch-progress-debug]', {
      event,
      ...(payload || {}),
    });
  }, []);

  const ensureDraftSession = useCallback(async (): Promise<string> => {
    if (!landingRevealAgent) {
      setLandingError('Please select an agent before starting a session.');
      throw new Error('No agent selected');
    }
    // If we already have an active session, reuse it instead of creating a new one.
    if (sessionId) return sessionId;
    if (draftSessionId) return draftSessionId;
    if (draftSessionPromiseRef.current) {
      return draftSessionPromiseRef.current;
    }
    const creation = (async () => {
      const response = await apiService.initializeSession({
        source: 'brief_builder',
        agentId: landingRevealAgent || undefined,
      });
      setDraftSessionId(response.session_id);
      return response.session_id;
    })();
    draftSessionPromiseRef.current = creation;
    try {
      return await creation;
    } finally {
      draftSessionPromiseRef.current = null;
    }
  }, [draftSessionId, landingRevealAgent, sessionId]);

  useEffect(() => {
    // When agent selection changes on the landing page, drop cached draft/workspace session
    draftSessionPromiseRef.current = null;
    setDraftSessionId(null);
    if (activeView === 'landing') {
      updateSessionId(null);
    }
  }, [landingRevealAgent, updateSessionId, activeView]);

  // Ensure brief builder is closed once we enter the workspace to avoid priming without an agent
  useEffect(() => {
    if (activeView === 'workspace' && showBriefBuilder) {
      setShowBriefBuilder(false);
    }
  }, [activeView, showBriefBuilder]);

  const [workspaceAccessRequested, setWorkspaceAccessRequested] = useState<boolean>(false);
  const {
    enabled: customReportsEnabled,
    customReports,
    createCustomReport,
    updateCustomReport,
    deleteCustomReport,
  } = useCustomReportProgram({ sessionId });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (activeView === 'workspace') {
      url.searchParams.set('page', 'workspace');
      if (url.hash === '#workspace') {
        url.hash = '';
      }
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [activeView]);

  useEffect(() => {
    if (activeView !== 'landing' && landingRevealAgent !== null) {
      setLandingRevealAgent(null);
    }
  }, [activeView, landingRevealAgent]);

  useEffect(() => {
    if (activeView !== 'workspace') {
      setShowWorkspaceOnboarding(false);
      setWorkspaceOnboardingOptOut(false);
      setOnboardingDismissed(false);
      setShowFirstTimeTour(false);
      setFirstTimeTourOptOut(false);
      setTourStep(null);
      isNewWorkspaceRef.current = false;
      pendingSessionNameModalRef.current = false;
      sessionNameModalDismissedForSessionRef.current = null;
      return;
    }
    
    const hideOnboarding =
      typeof window !== 'undefined' && window.localStorage?.getItem(WORKSPACE_ONBOARDING_HIDE_KEY) === '1';
    
    const shouldShow = isNewWorkspaceRef.current && !hideOnboarding;
    setShowWorkspaceOnboarding(shouldShow);
    
    // Show session name modal first for new workspaces (if onboarding is disabled)
    // First-time tour will be shown after session name modal is dismissed
    if (isNewWorkspaceRef.current && hideOnboarding && pendingSessionNameModalRef.current) {
      setShowSessionNameModal(true);
      pendingSessionNameModalRef.current = false;
      sessionNameModalDismissedForSessionRef.current = null;
    }
  }, [activeView]);

  const fetchSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const data = await apiService.listSessions();
      setAvailableSessions(data);
      setSessionsError(null);
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      console.error('Error fetching sessions:', err);
      setSessionsError('Failed to load previous workspaces');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const shouldPausePolling = useCallback(() => {
    return Date.now() < pollAuthBackoffUntilRef.current;
  }, []);

  const activateAuthBackoff = useCallback((durationMs: number = 12000) => {
    pollAuthBackoffUntilRef.current = Date.now() + durationMs;
  }, []);

  const handleCreateCustomReport = useCallback(() => {
    if (!customReportsEnabled) return;
    const created = createCustomReport();
    if (created) {
      setShouldAutoSelectLatest(false);
      pinnedReportIdRef.current = null;
      setSelectedReport(created);
    }
  }, [customReportsEnabled, createCustomReport]);

  const handleUpdateCustomReport = useCallback(
    (id: string, updates: Partial<MicroReport>) => {
      if (!customReportsEnabled) return;
      if (isCustomReportDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.log('[CustomReportDebug] handleUpdateCustomReport', {
          id,
          hasContent: typeof updates.content === 'string',
          contentLength: updates.content?.length ?? 0,
          hasTitle: typeof updates.title === 'string',
          titleLength: updates.title?.length ?? 0,
        });
      }
      if (selectedReport?.id === id) {
        const nextContent = updates.content ?? selectedReport.content;
        const nextTitle = updates.title ?? selectedReport.title;
        if (nextContent === selectedReport.content && nextTitle === selectedReport.title) {
          if (isCustomReportDebugEnabled()) {
            // eslint-disable-next-line no-console
            console.log('[CustomReportDebug] Skipping no-op custom report update', { id });
          }
          return;
        }
      }
      const updated = updateCustomReport(id, updates);
      if (updated && selectedReport?.id === id && updated !== selectedReport) {
        setSelectedReport(updated);
      } else if (isCustomReportDebugEnabled() && updated && updated === selectedReport) {
        // eslint-disable-next-line no-console
        console.log('[CustomReportDebug] update returned same reference; not updating selectedReport', { id });
      }
    },
    [customReportsEnabled, updateCustomReport, selectedReport]
  );

  const handleDeleteCustomReport = useCallback(
    (id: string) => {
      if (!customReportsEnabled) return;
      deleteCustomReport(id);
      setSelectedReport((prev) => (prev && prev.id === id ? null : prev));
    },
    [customReportsEnabled, deleteCustomReport]
  );

  const stopLandingStatusPolling = useCallback(() => {
    if (landingStatusPollerRef.current !== null) {
      window.clearInterval(landingStatusPollerRef.current);
      landingStatusPollerRef.current = null;
    }
  }, []);

  const mergeLandingStatus = useCallback((incomingSteps?: LandingProgressStep[]) => {
    const baseline = buildLandingProgressSteps();
    if (!incomingSteps || incomingSteps.length === 0) {
      setLandingProgressSteps(baseline);
      return;
    }
    const incomingMap = new Map(incomingSteps.map((step) => [step.id, step]));
    setLandingProgressSteps(
      baseline.map((step) => {
        const match = incomingMap.get(step.id);
        if (!match) {
          return step;
        }
        const nextStatus = (match.status || step.status) as LandingProgressStatus;
        return {
          ...step,
          status: nextStatus,
          progress: LANDING_STATUS_PROGRESS[nextStatus] ?? step.progress,
          detail: match.detail || '',
          timestamp: match.timestamp ?? null,
        };
      })
    );
  }, []);

  const stopLandingProgressAnimation = useCallback(() => {
    if (landingProgressAnimRef.current !== null) {
      window.clearInterval(landingProgressAnimRef.current);
      landingProgressAnimRef.current = null;
      logLaunchProgressDebug('progress_animation_stopped');
    }
  }, [logLaunchProgressDebug]);

  const setLandingProgressTarget = useCallback(
    (target: number, options?: { statusText?: string; allowDecrease?: boolean }) => {
      const previousTarget = landingProgressTargetRef.current;
      const normalizedTarget = Math.min(Math.max(Math.round(target), 0), 100);
      if (options?.allowDecrease) {
        landingProgressTargetRef.current = normalizedTarget;
      } else {
        landingProgressTargetRef.current = Math.max(
          landingProgressTargetRef.current,
          normalizedTarget
        );
      }

      if (options?.statusText && options.statusText.trim()) {
        setLandingProgressStatusText(options.statusText.trim());
      }

      if (landingProgressTargetRef.current !== previousTarget) {
        logLaunchProgressDebug('progress_target_updated', {
          phase: landingProgressPhaseRef.current,
          previous_target: previousTarget,
          next_target: landingProgressTargetRef.current,
          backend_signal_percent: landingBackendSignalRef.current,
          status_text: options?.statusText || null,
        });
      }
    },
    [logLaunchProgressDebug]
  );

  const startLandingProgressAnimation = useCallback(() => {
    stopLandingProgressAnimation();
    landingProgressAnimRef.current = window.setInterval(() => {
      const current = landingProgressPercentRef.current;
      const target = landingProgressTargetRef.current;
      const phase = landingProgressPhaseRef.current;

      // Trickle: even when at target, slowly creep toward a soft ceiling so bar
      // never appears frozen. Ceiling is 2% below next milestone boundary.
      const phaseCeilings: Record<string, number> = {
        backend: 78,
        reports: 93,
        frames: 98,
        done: 100,
      };
      const ceiling = phaseCeilings[phase] ?? 98;
      const effectiveTarget = Math.max(target, Math.min(current + 0.15, ceiling));

      if (effectiveTarget <= current) return;

      const gap = effectiveTarget - current;
      let step = 0.6;

      // Smooth backend phase progress (20 -> 80) with adaptive velocity.
      if (phase === 'backend') {
        if (gap > 15) step = 1.8;
        else if (gap > 8) step = 1.0;
        else if (gap > 3) step = 0.5;
        else step = 0.15;
      } else if (phase === 'reports') {
        step = 1.4;
      } else if (phase === 'frames') {
        step = 2.0;
      } else if (phase === 'done') {
        step = 4.0;
      }

      const next = Math.min(effectiveTarget, current + step);
      if (next !== current) {
        landingProgressPercentRef.current = next;
        const rounded = Math.round(next);
        setLandingProgressPercent(rounded);
        if (rounded !== landingLastLoggedPercentRef.current) {
          landingLastLoggedPercentRef.current = rounded;
          logLaunchProgressDebug('progress_tick', {
            phase: landingProgressPhaseRef.current,
            current: rounded,
            target: Math.round(effectiveTarget),
            gap: Math.round((effectiveTarget - next) * 10) / 10,
            step,
          });
        }
      }
    }, 200);
    logLaunchProgressDebug('progress_animation_started');
  }, [logLaunchProgressDebug, stopLandingProgressAnimation]);

  const fetchFrameGroups = useCallback(async (targetSessionId?: string | null) => {
    const sessionKey = targetSessionId ?? sessionId;
    if (!sessionKey) {
      setFrameGroups(null);
      return;
    }
    if (shouldPausePolling()) return;
    if (frameGroupsInFlightRef.current) return;
    try {
      frameGroupsInFlightRef.current = true;
      const response = await apiService.getSessionFrames(sessionKey);
      setFrameGroups(response);
    } catch (err) {
      const apiErr = err as Error & { status?: number };
      if (apiErr.status === 401 || apiErr.status === 403) {
        activateAuthBackoff();
      }
      console.warn('Failed to load frame groups', err);
      setFrameGroups(null);
    } finally {
      frameGroupsInFlightRef.current = false;
    }
  }, [sessionId, shouldPausePolling, activateAuthBackoff]);

  const resetLandingProgress = useCallback(() => {
    stopLandingStatusPolling();
    stopLandingProgressAnimation();
    landingStatusInFlightRef.current = false;
    launchReadinessGateActiveRef.current = false;
    latestLandingStatusUpdatedAtRef.current = 0;
    landingProgressPhaseRef.current = 'idle';
    landingBackendSignalRef.current = 0;
    landingProgressTargetRef.current = 0;
    landingProgressPercentRef.current = 0;
    landingLastLoggedPercentRef.current = 0;
    setLandingProgressSteps(buildLandingProgressSteps());
    setLandingProgressPercent(0);
    setLandingProgressStatusText('working (0%)');
    setShowLandingProgress(false);
    logLaunchProgressDebug('progress_reset');
  }, [logLaunchProgressDebug, stopLandingStatusPolling, stopLandingProgressAnimation]);

  // Comprehensive reset function to restore landing page to initial state (like first login)
  const handleResetLandingPage = useCallback(() => {
    // Reset all landing page state to initial values
    setLandingRevealAgent(null);
    setShowBriefBuilder(false);
    setLandingInput('');
    setLandingError(null);
    setDraftSessionId(null);
    resetLandingProgress();
    // Clear any pending brief prompts
    setPendingBriefPrompt(null);
  }, [resetLandingProgress]);

  const pollLandingStatus = useCallback(async (sessionKey: string) => {
    if (landingStatusInFlightRef.current) {
      return;
    }
    landingStatusInFlightRef.current = true;
    try {
      const response = await apiService.getLandingStatus(sessionKey);
      const signature = JSON.stringify({
        ready: Boolean(response?.ready),
        progress_percent: typeof response?.progress_percent === 'number' ? response.progress_percent : null,
        status_text: typeof response?.status_text === 'string' ? response.status_text : null,
        checks: response?.checks || null,
        blockers: response?.blockers || null,
      });
      if (landingStatusTelemetrySignatureRef.current !== signature) {
        landingStatusTelemetrySignatureRef.current = signature;
        console.log('[launch-debug] landing-status changed', {
          sessionId: sessionKey,
          ready: Boolean(response?.ready),
          progress_percent: response?.progress_percent ?? null,
          status_text: response?.status_text ?? null,
          checks: response?.checks ?? null,
          blockers: response?.blockers ?? null,
        });
        emitLaunchFlowEvent({
          event_name: 'landing_status_polled',
          session_id: sessionKey,
          ready: Boolean(response?.ready),
          progress_percent: typeof response?.progress_percent === 'number' ? response.progress_percent : undefined,
          status_text: typeof response?.status_text === 'string' ? response.status_text : undefined,
          checks: response?.checks,
          blockers: response?.blockers,
        });
      }
      if (response?.steps) {
        const updatedAtMs = Date.parse(response.updated_at || '') || 0;
        if (updatedAtMs < latestLandingStatusUpdatedAtRef.current) {
          return;
        }
        latestLandingStatusUpdatedAtRef.current = updatedAtMs;
        mergeLandingStatus(response.steps as LandingProgressStep[]);
      }
      if (landingProgressPhaseRef.current === 'backend') {
          const normalizedPercent =
            typeof response?.progress_percent === 'number'
              ? Math.min(Math.max(response.progress_percent, 0), 100)
              : 0;
          const completedArtifacts =
            typeof response?.completed_artifacts === 'number'
              ? Math.max(0, response.completed_artifacts)
              : null;
          const totalArtifacts =
            typeof response?.total_artifacts === 'number'
              ? Math.max(0, response.total_artifacts)
              : null;

          // Derive a stable backend signal. Prefer artifact completion ratio when available,
          // because raw progress_percent can regress or jump via stage override.
          let backendSignalPercent = normalizedPercent;
          if (completedArtifacts !== null && totalArtifacts !== null && totalArtifacts > 0) {
            backendSignalPercent = Math.round(
              Math.min(Math.max((completedArtifacts / totalArtifacts) * 100, 0), 100)
            );
          } else if (response?.checks && typeof response.checks === 'object') {
            const checkValues = Object.values(response.checks).filter(
              (value): value is boolean => typeof value === 'boolean'
            );
            if (checkValues.length > 0) {
              const completedChecks = checkValues.filter(Boolean).length;
              backendSignalPercent = Math.round((completedChecks / checkValues.length) * 100);
            }
          }

          landingBackendSignalRef.current = Math.max(
            landingBackendSignalRef.current,
            backendSignalPercent
          );
          const phaseATarget = 20 + (landingBackendSignalRef.current / 100) * 60;
          setLandingProgressTarget(phaseATarget, {
            statusText:
              typeof response?.status_text === 'string' && response.status_text.trim()
                ? response.status_text.trim()
                : `Preparing workspace… (${Math.round(phaseATarget)}%)`,
          });
          logLaunchProgressDebug('backend_signal_updated', {
            raw_progress_percent: normalizedPercent,
            derived_backend_signal_percent: backendSignalPercent,
            monotonic_backend_signal_percent: landingBackendSignalRef.current,
            completed_artifacts: completedArtifacts,
            total_artifacts: totalArtifacts,
            checks: response?.checks || null,
            ready: Boolean(response?.ready),
          });
      }
    } catch (err) {
      // Session might not be initialized yet; ignore transient failures.
    } finally {
      landingStatusInFlightRef.current = false;
    }
  }, [emitLaunchFlowEvent, mergeLandingStatus, setLandingProgressTarget]);

  const startLandingStatusMonitor = useCallback((sessionKey: string) => {
    stopLandingStatusPolling();
    stopLandingProgressAnimation();
    latestLandingStatusUpdatedAtRef.current = 0;
    landingStatusInFlightRef.current = false;
    landingProgressPhaseRef.current = 'backend';
    landingBackendSignalRef.current = 0;
    landingProgressPercentRef.current = 20;
    landingProgressTargetRef.current = 20;
    landingLastLoggedPercentRef.current = 20;
    setShowLandingProgress(true);
    setLandingProgressPercent(20);
    setLandingProgressStatusText('Preparing workspace…');
    logLaunchProgressDebug('progress_monitor_started', {
      session_id: sessionKey,
      phase: landingProgressPhaseRef.current,
      current: 20,
      target: 20,
    });
    mergeLandingStatus();
    startLandingProgressAnimation();
    void pollLandingStatus(sessionKey);
    landingStatusPollerRef.current = window.setInterval(() => {
      void pollLandingStatus(sessionKey);
    }, 1500);
  }, [
    logLaunchProgressDebug,
    mergeLandingStatus,
    pollLandingStatus,
    startLandingProgressAnimation,
    stopLandingProgressAnimation,
    stopLandingStatusPolling,
  ]);

  useEffect(() => {
    return () => {
      stopLandingProgressAnimation();
    };
  }, [stopLandingProgressAnimation]);

  // Load microreports for the active (or provided) session
  const latestFetchIdRef = useRef<string | null>(null);
  const pinnedReportIdRef = useRef<string | null>(null);
  const scopeAutoSelectedRef = useRef<boolean>(false);

  const fetchReportById = useCallback(async (reportId: string) => {
    if (shouldPausePolling()) return;
    const now = Date.now();
    const lastRefreshAt = reportRefreshLastAtRef.current.get(reportId) ?? 0;
    if (reportRefreshInFlightRef.current.has(reportId)) return;
    if (now - lastRefreshAt < 5000) return;
    console.log('[App] fetchReportById called for:', reportId);
    latestFetchIdRef.current = reportId;
    reportRefreshInFlightRef.current.add(reportId);
    reportRefreshLastAtRef.current.set(reportId, now);
    try {
      const fresh = await apiService.getMicroReport(reportId);
      console.log('[App] fetchReportById received:', {
        id: fresh.id,
        title: fresh.title,
        type: fresh.type,
        contentLength: fresh.content?.length || 0,
        contentStart: fresh.content?.substring(0, 100) || ''
      });
      if (latestFetchIdRef.current !== reportId) return;
      setSelectedReport(fresh);
    } catch (err) {
      const apiErr = err as Error & { status?: number };
      if (latestFetchIdRef.current === reportId) {
        latestFetchIdRef.current = null;
      }
      if (isAbortError(err)) {
        return;
      }
      if (apiErr.status === 404) {
        if (pinnedReportIdRef.current === reportId) {
          pinnedReportIdRef.current = null;
        }
        setSelectedReport((prev) => (prev?.id === reportId ? null : prev));
        setShouldAutoSelectLatest(true);
        return;
      }
      if (apiErr.status === 401 || apiErr.status === 403) {
        activateAuthBackoff();
      }
      console.error('Failed to refresh report content', err);
    } finally {
      reportRefreshInFlightRef.current.delete(reportId);
    }
  }, [shouldPausePolling, activateAuthBackoff]);

  const loadMicroreports = useCallback(async (options?: { sessionId?: string | null; autoSelectLatest?: boolean }) => {
    if (shouldPausePolling()) return;
    if (microreportsInFlightRef.current) return;
    const currentSelection = selectedReport;
    const targetSessionId = options?.sessionId ?? sessionId ?? null;
    const autoSelectLatest = options?.autoSelectLatest ?? false;
    if (autoSelectLatest) {
      setShouldAutoSelectLatest(true);
    }
    try {
      microreportsInFlightRef.current = true;
      setLoading(true);
      setError(null);
      const apiReports = targetSessionId
        ? await apiService.getSessionReports(targetSessionId)
        : await apiService.getMicroReports();
      const reports: MicroReport[] = apiReports.map(report => ({
        id: report.id,
        title: report.title,
        date: report.date,
        content: report.content,
        type: report.type,
        map: report.map ?? null
      }));
      const combinedReports = scopeReport
        ? [...reports.filter((r) => (r.type || '').toLowerCase() !== 'scope'), scopeReport]
        : reports;
      setMicroreports(combinedReports);
      const hasStepReports = combinedReports.some((r) => (r.type || '').toLowerCase() === 'step');
      const hasFinalReports = combinedReports.some((r) => (r.type || '').toLowerCase() === 'final');
      const hasResearchPlan = combinedReports.some((r) => (r.type || '').toLowerCase() === 'plan');
      if (!microreportsVisible && (hasResearchPlan || hasStepReports || hasFinalReports)) {
        setMicroreportsVisible(true);
      }
      if (!journeyUnlocked) {
        if (hasStepReports || hasFinalReports) {
          setJourneyUnlocked(true);
        }
      }
      const shouldUnlockFrames = hasResearchPlan && !hasStepReports && !hasFinalReports;
      if (shouldUnlockFrames) {
        setCompletedFrameNumbers([]);
      }
      const hasCustomSelection = !!currentSelection && (currentSelection.type || '').toLowerCase() === 'custom';
      const resolvedSelection = (() => {
        if (combinedReports.length === 0) return null;
        const currentSelectionExists = !!currentSelection && combinedReports.some((report) => report.id === currentSelection.id);
        const pinnedId = pinnedReportIdRef.current;
        const shouldForceAuto = (autoSelectLatest || shouldAutoSelectLatest) && !pinnedId && !currentSelectionExists;
        if (shouldForceAuto) {
          const scope = combinedReports.find((r) => (r.type || '').toLowerCase() === 'scope');
          return scope ?? combinedReports[combinedReports.length - 1];
        }
        if (pinnedId) {
          const pinnedMatch = combinedReports.find((r) => r.id === pinnedId);
          if (pinnedMatch) return pinnedMatch;
          pinnedReportIdRef.current = null;
        }
        if (currentSelection) {
          const match = combinedReports.find((r) => r.id === currentSelection.id);
          if (match) return match;
        }
        return combinedReports[combinedReports.length - 1];
      })();
      const isScopeSelection =
        !!currentSelection && (currentSelection.type || '').toLowerCase() === 'scope';
      const pinnedScopeSelection = isScopeSelection
        ? combinedReports.find((report) => report.id === currentSelection?.id) || scopeReport || currentSelection
        : null;
      const nextSelection = hasCustomSelection
        ? currentSelection
        : (isScopeSelection ? pinnedScopeSelection : resolvedSelection);
      if (!hasCustomSelection) {
        setSelectedReport(nextSelection);
      }
      const normalizedNextType = (nextSelection?.type || '').toLowerCase();
      if (nextSelection && normalizedNextType !== 'custom' && normalizedNextType !== 'scope') {
        void fetchReportById(nextSelection.id);
      }
      const framesSession = targetSessionId ?? sessionId ?? null;
      if (framesSession) {
        void fetchFrameGroups(framesSession);
      } else {
        setFrameGroups(null);
      }
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      const apiErr = err as Error & { status?: number };
      if (apiErr.status === 401 || apiErr.status === 403) {
        activateAuthBackoff();
        setSessionAccessError('Your session is no longer authorized. Please sign in again and relaunch the workspace.');
        setIsLandingParsing(false);
        setIsAnalysisGenerationBusy(false);
        setShowLandingProgress(false);
        return;
      }
      if (microreports.length === 0) {
        setError('Failed to load microreports');
      }
      console.error('Error loading microreports:', err);
    } finally {
      microreportsInFlightRef.current = false;
      setLoading(false);
    }
  }, [sessionId, shouldAutoSelectLatest, setShouldAutoSelectLatest, fetchReportById, fetchFrameGroups, scopeReport, journeyUnlocked, microreports.length, shouldPausePolling, activateAuthBackoff]);

  useEffect(() => {
    if (!scopeReport) return;
    setMicroreports((prev) => {
      const withoutScope = prev.filter((r) => (r.type || '').toLowerCase() !== 'scope');
      return [...withoutScope, scopeReport];
    });
  }, [scopeReport]);

  useEffect(() => {
    if (!scopeReport) return;
    // Only auto-select scope during brand-new workspace opens.
    // Resumed workspaces should preserve latest/previous report selection.
    if (!isNewWorkspaceRef.current) return;
    const normalizedSelectedType = (selectedReport?.type || '').toLowerCase();
    const scopeAlreadySelected = normalizedSelectedType === 'scope';

    if (scopeAlreadySelected && selectedReport?.id !== scopeReport.id) {
      emitLaunchFlowEvent({
        event_name: 'scope_ui_reselect_existing_scope',
        session_id: sessionId || draftSessionId || undefined,
        previous_selected_report_id: selectedReport?.id || null,
        next_scope_report_id: scopeReport.id,
      });
      setSelectedReport(scopeReport);
      return;
    }

    if (scopeAutoSelectedRef.current) return;
    if (pinnedReportIdRef.current) return;

    scopeAutoSelectedRef.current = true;
    pinnedReportIdRef.current = scopeReport.id;
    emitLaunchFlowEvent({
      event_name: 'scope_ui_auto_selected',
      session_id: sessionId || draftSessionId || undefined,
      scope_report_id: scopeReport.id,
      selected_report_type: (selectedReport?.type || '').toLowerCase() || null,
      is_new_workspace: true,
    });
    setSelectedReport(scopeReport);
  }, [scopeReport, selectedReport, emitLaunchFlowEvent, sessionId, draftSessionId]);

  useEffect(() => {
    const activeSessionId = sessionId || draftSessionId || undefined;
    const payload = {
      active_session_id: activeSessionId || null,
      selected_report_id: selectedReport?.id || null,
      selected_report_type: (selectedReport?.type || '').toLowerCase() || null,
      scope_report_id: scopeReport?.id || null,
      scope_auto_selected: scopeAutoSelectedRef.current,
      is_new_workspace: isNewWorkspaceRef.current,
    };
    const signature = JSON.stringify(payload);
    if (scopeSelectionTelemetrySignatureRef.current === signature) {
      return;
    }
    scopeSelectionTelemetrySignatureRef.current = signature;
    emitLaunchFlowEvent({
      event_name: 'scope_ui_selection_changed',
      session_id: activeSessionId,
      ...payload,
    });
  }, [selectedReport, scopeReport, sessionId, draftSessionId, emitLaunchFlowEvent]);

  const handleSelectReport = useCallback((report: MicroReport) => {
    console.log('[App] handleSelectReport called:', {
      id: report.id,
      title: report.title,
      type: report.type,
      contentLength: report.content?.length || 0
    });
    setShouldAutoSelectLatest(false);
    const normalizedType = (report.type || '').toLowerCase();
    if (normalizedType === 'custom') {
      pinnedReportIdRef.current = null;
      latestFetchIdRef.current = null;
      setSelectedReport(report);
      return;
    }
    pinnedReportIdRef.current = report.id;
    setSelectedReport(report);
    if (report?.id && normalizedType !== 'scope') {
      void fetchReportById(report.id);
    }
  }, [fetchReportById]);

  const handleSaveSessionName = useCallback(async () => {
    const trimmed = sessionNameInput.trim();
    if (!trimmed) return;
    try {
      setSavingSessionName(true);
      // Never create/switch sessions from the naming modal.
      // If state is briefly behind during launch, wait for active session id to sync.
      const waitForActiveSessionId = async (
        timeoutMs: number = 5000,
        intervalMs: number = 200
      ): Promise<string | null> => {
        const readCandidate = (): string | null => {
          const apiSessionId = apiService.getSessionId();
          if (apiSessionId) return apiSessionId;
          if (typeof window === 'undefined') return null;
          return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        };
        const immediate = sessionId || readCandidate();
        if (immediate) return immediate;
        const startedAt = Date.now();
        while (Date.now() - startedAt < timeoutMs) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, intervalMs));
          const candidate = readCandidate();
          if (candidate) return candidate;
        }
        return readCandidate();
      };

      const targetSessionId = await waitForActiveSessionId();
      if (!targetSessionId) {
        window.alert('Workspace is still initializing. Please retry naming in a few seconds.');
        return;
      }
      apiService.setSessionId(targetSessionId);

      void applySuccessfulSessionName({
        targetSessionId,
        sessionName: trimmed,
        agentId: landingRevealAgent,
        setSessionName,
        setSessionNameInput,
        closeSessionNameModal: () => setShowSessionNameModal(false),
        setAvailableSessions,
        onAfterClose: () => {
          sessionNameModalDismissedForSessionRef.current = targetSessionId;
          const hideFirstTimeTour =
            typeof window !== 'undefined' && window.localStorage?.getItem(FIRST_TIME_TOUR_HIDE_KEY) === '1';
          if (isNewWorkspaceRef.current && !hideFirstTimeTour) {
            setShowFirstTimeTour(true);
          }
        },
      }).catch((refreshError) => {
        console.error('Failed to refresh sessions after saving session name:', refreshError);
      });

      // Optimistic UX: move forward immediately and persist the name asynchronously.
      void (async () => {
        try {
          await apiService.setSessionName(trimmed);
          await fetchSessions();
        } catch (err: any) {
          const msg = String(err?.message || err || '');
          console.error('Failed to save session name in background:', err);
          window.alert(
            msg.toLowerCase().includes('not found') || msg.includes('404')
              ? 'Workspace name could not be saved to the current session. Please retry naming this workspace.'
              : 'Workspace opened, but name save is still syncing. Please retry if the name does not appear.'
          );
        }
      })();
    } catch (err) {
      console.error('Failed to save session name:', err);
    } finally {
      setSavingSessionName(false);
    }
  }, [sessionNameInput, fetchSessions, sessionId, landingRevealAgent]);

  const handleDeleteSession = useCallback(async (targetSessionId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this workspace? This will remove the workspace and all related reports.'
    );
    if (!confirmed) return;
    try {
      setDeletingSessionId(targetSessionId);
      await apiService.deleteSession(targetSessionId);
      setAvailableSessions((prev) => prev.filter((entry) => entry.session_id !== targetSessionId));
      if (sessionId === targetSessionId) {
        updateSessionId(null);
        setSelectedReport(null);
        setMicroreports([]);
        setStepStatuses([]);
        setJourneyStarted(false);
        setJourneyUnlocked(false);
        setActiveView('landing');
      }
      await fetchSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setSessionsError('Failed to delete workspace');
      window.setTimeout(() => setSessionsError(null), 4000);
    } finally {
      setDeletingSessionId(null);
    }
  }, [sessionId, updateSessionId, fetchSessions]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    loadMicroreports();
    fetchSessions();
  }, [fetchSessions, isSignedIn, loadMicroreports]);

  useEffect(() => {
    const onWindowError = (event: ErrorEvent) => {
      console.error('[launch-debug] window.onerror', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        sessionId: apiService.getSessionId(),
      });
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      console.error('[launch-debug] window.onunhandledrejection', {
        reason: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
        sessionId: apiService.getSessionId(),
      });
    };
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) {
      return;
    }
    apiService.invalidateCreditsCache();
    void refreshCredits();
    const intervalId = window.setInterval(() => {
      apiService.invalidateCreditsCache();
      void refreshCredits();
    }, 10000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAuthLoaded, isSignedIn, refreshCredits]);

  useEffect(() => {
    return () => {
      stopLandingStatusPolling();
    };
  }, [stopLandingStatusPolling]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleBeforeUnload = () => {
      if (!draftSessionId || workspaceAccessRequested) return;
      try {
        const url = `${API_BASE_URL}/sessions/${draftSessionId}/abandon`;
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } catch (err) {
        console.warn('Failed to send abandon beacon', err);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [draftSessionId, workspaceAccessRequested]);

  const handleAddToScratchpad = useCallback((item: any, category?: string) => {
    const targetCat = (category || 'Ungrouped').trim() || 'Ungrouped';
    const scratchHasCat = scratchpadItems.some((it) => (it.category || 'Ungrouped') === targetCat);
    const canvasHasCat = canvasCategoriesRef.current.includes(targetCat);

    if (!scratchHasCat && canvasHasCat) {
      const payload = { id: Date.now(), category: targetCat, content: String(item.content || '') };
      // Queue globally in case canvas is not mounted
      (window as any).__canvasPendingAdds = (window as any).__canvasPendingAdds || [];
      (window as any).__canvasPendingAdds.push(payload);
      // Fire event for live canvas
      window.dispatchEvent(new CustomEvent('canvas-add', { detail: payload }));
      return;
    }
    setScratchpadItems([...scratchpadItems, { ...item, id: Date.now(), category: targetCat }]);
  }, [scratchpadItems]);

  const handleAddCategory = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCategories((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
  }, []);

  const handleRemoveScratchpadItem = useCallback((id: number) => {
    setScratchpadItems((prev) => prev.filter(item => item.id !== id));
  }, []);

  const handleCanvasCategoriesChange = useCallback((names: string[]) => {
    canvasCategoriesRef.current = names || [];
  }, []);

  const fetchStepStatus = useCallback(async (
    currentSessionId: string,
    options?: { force?: boolean }
  ) => {
    if (shouldPausePolling()) return null;
    const force = options?.force === true;
    const now = Date.now();
    if (!force && now - stepStatusLastAtRef.current < stepStatusMinIntervalMs) {
      return null;
    }
    if (stepStatusInFlightRef.current) return null;
    try {
      stepStatusInFlightRef.current = true;
      stepStatusLastAtRef.current = now;
      setSessionAccessError(null);
      const status = await apiService.getStepStatus(currentSessionId);
      const sorted = (status.steps || []).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));
      setStepStatuses(sorted);
      if (sorted.length > 0) {
        setError(null);
      }
      setSelectedMicroreportIds(Array.isArray(status.selected_microreport_ids) ? status.selected_microreport_ids : []);
      setStepStage(status.stage || null);
      setFinalRegenerating(
        status.final_regenerating === true || status.stage === 'final_report_running'
      );
      const reportsReady = status.reports_ready_to_generate === true;
      const completed = Array.isArray(status.completed_frames)
        ? Array.from(
            new Set(
              status.completed_frames
                .map((value) => Number(value))
                .filter((value) => Number.isFinite(value))
                .map((value) => Math.trunc(value))
                .filter((value) => value > 0)
            )
          ).sort((a, b) => a - b)
        : [];
      setCompletedFrameNumbers(completed);
      // Keep frames selected until backend explicitly marks them completed.
      setSelectedFrameNumbers((prev) => {
        if (!prev.length || !completed.length) return prev;
        const completedSet = new Set(completed);
        const next = prev.filter((n) => !completedSet.has(n));
        return next.length === prev.length ? prev : next;
      });
      const stageValue = String(status.stage || '').toLowerCase();
      const hasStepProgress = sorted.some((step) => {
        const stepStatus = (step.status || '').toLowerCase();
        return stepStatus === 'in_progress' || stepStatus === 'completed' || !!step.report_id;
      });
      const startedProcessing = isAnalysisGenerationStage(stageValue);
      if (analysisQueuePending && startedProcessing) {
        setAnalysisQueuePending(false);
        setAugmentationStatusMessage(null);
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(queueReloadStorageKey);
        }
      }
      if (!microreportsVisible) {
        const hasSubmittedFrames = completed.length > 0 || reportsReady || (status.stage || '').startsWith('frame_selection');
        if (hasSubmittedFrames) {
          setMicroreportsVisible(true);
        }
      }
      
      // Check if augmentation worker is running
      const augmentationRunning = status.augmentation_worker_running === true;
      setAugmentationWorkerRunning(augmentationRunning);
      
      // If augmentation just completed, refresh report sections
      if (!augmentationRunning && augmentationWorkerRunning) {
        setTimeout(() => {
          fetchReportSections(currentSessionId, { force: true });
        }, 500); // Small delay to ensure file is written
      }
      
      if (!journeyUnlocked) {
        const stageComplete = stageValue.startsWith('step_research');
        const frameSelectionReady = stageValue.startsWith('frame_selection');
        if (stageComplete || frameSelectionReady || hasStepProgress) {
          setJourneyUnlocked(true);
        }
      }
      if (status.session_name !== undefined) {
        const incoming = (status.session_name || '').trim();
        setSessionName(incoming);
        if (!shouldShowSessionNameModal(incoming)) {
          // Session is already named: sync the input to canonical value.
          sessionNameModalDismissedForSessionRef.current = currentSessionId;
          setSessionNameInput((prev) => (prev === incoming ? prev : incoming));
          setShowSessionNameModal(false);
        } else {
          if (sessionNameModalDismissedForSessionRef.current === currentSessionId) {
            setShowSessionNameModal(false);
            return {
              stage: status.stage || null,
              steps: sorted,
            };
          }
          // Keep the user's in-progress typing while polling continues.
          setSessionNameInput((prev) => prev);
          setShowSessionNameModal(true);
        }
      }
      return {
        stage: status.stage || null,
        steps: sorted,
      };
    } catch (err) {
      if (isAbortError(err)) {
        return null;
      }
      console.error('Error fetching step status:', err);
      const apiErr = err as Error & { status?: number };
      if (apiErr.status === 401 || apiErr.status === 403) {
        activateAuthBackoff();
        setSessionAccessError('Your session is no longer authorized. Please sign in again and relaunch the workspace.');
        setIsLandingParsing(false);
        setIsAnalysisGenerationBusy(false);
        setShowLandingProgress(false);
      }
      return null;
    } finally {
      stepStatusInFlightRef.current = false;
    }
  }, [journeyUnlocked, augmentationWorkerRunning, analysisQueuePending, isAnalysisGenerationStage, shouldPausePolling, activateAuthBackoff, stepStatusMinIntervalMs]);

  const fetchReportSections = useCallback(async (
    currentSessionId: string,
    options?: { force?: boolean }
  ) => {
    if (shouldPausePolling()) return;
    const force = options?.force === true;
    const now = Date.now();
    if (!force && now - reportSectionsLastAtRef.current < reportSectionsMinIntervalMs) {
      return;
    }
    if (reportSectionsInFlightRef.current) return;
    try {
      reportSectionsInFlightRef.current = true;
      reportSectionsLastAtRef.current = now;
      setSessionAccessError(null);
      emitLaunchFlowEvent({
        event_name: 'scope_sections_fetch_start',
        session_id: currentSessionId,
        force,
        active_view: activeView,
        launch_gate_active: launchReadinessGateActiveRef.current,
      });
      const sections = await apiService.getReportSections(currentSessionId);
      if (!sections && launchReadinessGateActiveRef.current) {
        // During first-time startup, missing sections means "still initializing", not "empty".
        emitLaunchFlowEvent({
          event_name: 'scope_sections_fetch_gate_wait',
          session_id: currentSessionId,
          active_view: activeView,
          launch_gate_active: true,
        });
        return;
      }
      // Keep report sections as soon as backend returns them; gating on
      // microreportsVisible can hide scope/prework during launch transitions.
      emitLaunchFlowEvent({
        event_name: 'scope_sections_fetch_success',
        session_id: currentSessionId,
        has_sections: Boolean(sections),
        microreport_count: sections?.microreports?.length ?? 0,
        has_scope: Boolean(sections?.scope && sections.scope.trim()),
        has_context: Boolean(sections?.context && sections.context.trim()),
        has_competitors: Boolean(sections?.competitors && sections.competitors.trim()),
        has_trend_categories: Boolean(
          sections?.trend_categories && sections.trend_categories.trim()
        ),
      });
      setReportSections(sections);
    } catch (err) {
      const apiErr = err as Error & { status?: number };
      if (apiErr.status === 401 || apiErr.status === 403) {
        activateAuthBackoff();
        console.error('Report sections request lost authorization:', err);
        setSessionAccessError('Your session is no longer authorized. Please sign in again and relaunch the workspace.');
        setIsLandingParsing(false);
        setIsAnalysisGenerationBusy(false);
        setShowLandingProgress(false);
        emitLaunchFlowEvent({
          event_name: 'scope_sections_fetch_unauthorized',
          session_id: currentSessionId,
          status: apiErr.status,
          active_view: activeView,
        });
        return;
      }
      if (launchReadinessGateActiveRef.current) {
        // Keep prior UI state while launch readiness is still in progress.
        emitLaunchFlowEvent({
          event_name: 'scope_sections_fetch_error_gate_wait',
          session_id: currentSessionId,
          active_view: activeView,
        });
        return;
      }
      console.debug('Report sections not available yet:', err);
      emitLaunchFlowEvent({
        event_name: 'scope_sections_fetch_error_reset',
        session_id: currentSessionId,
        active_view: activeView,
        error_message: err instanceof Error ? err.message : String(err),
      });
      setReportSections(null);
    } finally {
      reportSectionsInFlightRef.current = false;
    }
  }, [shouldPausePolling, activateAuthBackoff, reportSectionsMinIntervalMs, emitLaunchFlowEvent, activeView]);

  useEffect(() => {
    const activeSessionId = sessionId || draftSessionId || undefined;
    const payload = {
      active_session_id: activeSessionId || null,
      active_view: activeView,
      has_report_sections: Boolean(reportSections),
      microreport_count: reportSections?.microreports?.length ?? 0,
      has_scope: Boolean(reportSections?.scope && reportSections.scope.trim()),
      has_context: Boolean(reportSections?.context && reportSections.context.trim()),
      has_competitors: Boolean(
        reportSections?.competitors && reportSections.competitors.trim()
      ),
      has_trend_categories: Boolean(
        reportSections?.trend_categories && reportSections.trend_categories.trim()
      ),
      has_scope_report: Boolean(scopeReport),
      scope_report_id: scopeReport?.id || null,
      selected_report_id: selectedReport?.id || null,
      selected_report_type: (selectedReport?.type || '').toLowerCase() || null,
      is_new_workspace: isNewWorkspaceRef.current,
      scope_auto_selected: scopeAutoSelectedRef.current,
      pinned_report_id: pinnedReportIdRef.current || null,
    };
    const signature = JSON.stringify(payload);
    if (scopeSectionsTelemetrySignatureRef.current === signature) {
      return;
    }
    scopeSectionsTelemetrySignatureRef.current = signature;
    emitLaunchFlowEvent({
      event_name: 'scope_ui_binding_state',
      session_id: activeSessionId,
      ...payload,
    });
  }, [reportSections, scopeReport, selectedReport, sessionId, draftSessionId, activeView, emitLaunchFlowEvent]);

  const handleKillGeneration = useCallback(async () => {
    if (!sessionId || isKillingGeneration) return;
    const confirmed = window.confirm(
      'Stop all analysis generation for this workspace now?'
    );
    if (!confirmed) return;
    try {
      setIsKillingGeneration(true);
      await apiService.killGeneration(sessionId);
      setAnalysisQueuePending(false);
      setIsAnalysisGenerationBusy(false);
      setAugmentationWorkerRunning(false);
      setAugmentationStatusMessage('Generation was stopped.');
      await Promise.allSettled([
        fetchStepStatus(sessionId, { force: true }),
        loadMicroreports({ sessionId }),
        fetchReportSections(sessionId, { force: true }),
      ]);
    } catch (err) {
      console.error('Failed to stop generation:', err);
      window.alert(
        err instanceof Error ? err.message : 'Failed to stop generation.'
      );
    } finally {
      setIsKillingGeneration(false);
    }
  }, [
    sessionId,
    isKillingGeneration,
    fetchStepStatus,
    loadMicroreports,
    fetchReportSections,
  ]);

  const activeSessionForSections = useMemo(() => sessionId || draftSessionId, [sessionId, draftSessionId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const raw = window.sessionStorage.getItem(queueReloadStorageKey);
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as { sessionId?: string; startedAt?: number };
      const storedSessionId = String(parsed?.sessionId || '').trim();
      const startedAt = Number(parsed?.startedAt || 0);
      if (!storedSessionId || !Number.isFinite(startedAt) || startedAt <= 0) {
        window.sessionStorage.removeItem(queueReloadStorageKey);
        return;
      }
      if (!sessionId) {
        updateSessionId(storedSessionId);
      }
      if (activeView === 'workspace') {
        setAnalysisQueuePending(true);
        if (!augmentationStatusMessage) {
          setAugmentationStatusMessage('Queuing up your analysis...');
        }
      }
    } catch (_error) {
      window.sessionStorage.removeItem(queueReloadStorageKey);
    }
  }, [activeView, sessionId, updateSessionId, augmentationStatusMessage]);

  useEffect(() => {
    if (!analysisQueuePending || !sessionId) {
      return;
    }
    let stopped = false;
    const pollQueuedAnalysis = async () => {
      if (shouldPausePolling()) return;
      const snapshot = await fetchStepStatus(sessionId);
      if (stopped) {
        return;
      }
      await loadMicroreports({ sessionId });
      await fetchReportSections(sessionId);
      if (stopped || !snapshot) {
        return;
      }
      const stageValue = String(snapshot.stage || '').toLowerCase();
      const startedProcessing = isAnalysisGenerationStage(stageValue);
      if (startedProcessing) {
        setAnalysisQueuePending(false);
        setAugmentationStatusMessage(null);
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(queueReloadStorageKey);
        }
        return;
      }
      if (typeof window !== 'undefined') {
        const raw = window.sessionStorage.getItem(queueReloadStorageKey);
        let startedAt = Date.now();
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { startedAt?: number };
            const parsedStartedAt = Number(parsed?.startedAt || 0);
            if (Number.isFinite(parsedStartedAt) && parsedStartedAt > 0) {
              startedAt = parsedStartedAt;
            }
          } catch (_error) {
          }
        }
        const elapsed = Date.now() - startedAt;
        if (elapsed >= queueReloadAfterMs && activeView === 'workspace' && document.visibilityState === 'visible') {
          window.location.reload();
        }
      }
    };
    void pollQueuedAnalysis();
    const interval = window.setInterval(() => {
      if (shouldPausePolling()) return;
      void pollQueuedAnalysis();
    }, queueReloadIntervalMs);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [analysisQueuePending, sessionId, fetchStepStatus, loadMicroreports, fetchReportSections, queueReloadAfterMs, queueReloadIntervalMs, activeView, isAnalysisGenerationStage, shouldPausePolling]);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) return;
    if (shouldPausePolling()) return;
    if (activeSessionForSections) {
      fetchStepStatus(activeSessionForSections);
      fetchReportSections(activeSessionForSections);

      const shouldPollStepStatus =
        augmentationWorkerRunning ||
        finalRegenerating ||
        analysisQueuePending ||
        stepStage === 'awaiting_report_generation' ||
        stepStage === 'step_research_auto_running' ||
        stepStage === 'step_research_incomplete' ||
        stepStage === 'final_report_running';

      // Avoid overlapping queue-pending poller and generation poller.
      if (shouldPollStepStatus && !analysisQueuePending) {
        const interval = setInterval(() => {
          if (shouldPausePolling()) return;
          fetchStepStatus(activeSessionForSections);
          loadMicroreports();
        }, reportGenerationPollIntervalMs);
        return () => clearInterval(interval);
      }
    }
  }, [activeSessionForSections, fetchStepStatus, fetchReportSections, loadMicroreports, augmentationWorkerRunning, finalRegenerating, analysisQueuePending, stepStage, isAuthLoaded, isSignedIn, shouldPausePolling, reportGenerationPollIntervalMs]);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) return;
    if (!sessionId) return;
    // Keep only one active poll profile at a time.
    if (analysisQueuePending || stepStage === 'awaiting_report_generation') return;
    const allStepsCompleted = stepStatuses.length > 0 && stepStatuses.every((step) => {
      const status = (step.status || '').toLowerCase();
      return status === 'completed' || !!step.report_id;
    });
    const hasFinalReport = microreports.some((report) => (report.type || '').toLowerCase() === 'final');
    if (stepStage !== 'final_report_running' && !(allStepsCompleted && !hasFinalReport) && !finalRegenerating) return;
    const interval = setInterval(() => {
      if (shouldPausePolling()) return;
      loadMicroreports();
      fetchStepStatus(sessionId);
    }, reportGenerationPollIntervalMs);
    return () => clearInterval(interval);
  }, [sessionId, stepStage, stepStatuses, microreports, loadMicroreports, fetchStepStatus, isAuthLoaded, isSignedIn, finalRegenerating, shouldPausePolling, reportGenerationPollIntervalMs]);

  useEffect(() => {
    researchAutoStartRequestedRef.current = false;
    setCreditSupportError(null);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (!stepStage) return;
    if (stepStage === 'step_research_auto_running') return;
    // Whenever stage changes (e.g., to complete), immediately refresh
    loadMicroreports();
    fetchStepStatus(sessionId);
    // Refresh credits when research completes (credits are deducted per report)
    apiService.invalidateCreditsCache();
    void refreshCredits();
  }, [sessionId, stepStage, loadMicroreports, fetchStepStatus, refreshCredits]);

  useEffect(() => {
    if (!sessionId) return;
    if (!workspaceAccessRequested) return;
    if (!journeyStarted) setJourneyStarted(true);
    if (activeView !== 'workspace' && !showLandingProgress) setActiveView('workspace');
  }, [sessionId, journeyStarted, activeView, showLandingProgress, workspaceAccessRequested]);

  // Track step reports count and refresh credits when new reports are generated
  const stepReportsCount = microreports.filter((r) => (r.type || '').toLowerCase() === 'step').length;
  const prevStepReportsCountRef = useRef(stepReportsCount);
  useEffect(() => {
    if (stepReportsCount > prevStepReportsCountRef.current) {
      // New step report generated - refresh credits
      apiService.invalidateCreditsCache();
      void refreshCredits();
    }
    prevStepReportsCountRef.current = stepReportsCount;
  }, [stepReportsCount, refreshCredits]);

  const handleChatMessage = useCallback(async (
    message: string,
    options?: { onToken?: (chunk: string) => void }
  ): Promise<ChatResponse> => {
    try {
      setIsChatBusy(true);
      const response = options?.onToken
        ? await apiService.streamStrategyChat(message, options.onToken, landingRevealAgent || undefined)
        : await apiService.strategyChat(message, landingRevealAgent || undefined);
      if (!journeyStarted) setJourneyStarted(true);
      updateSessionId(response.session_id);
      void loadMicroreports({ sessionId: response.session_id });
      void fetchStepStatus(response.session_id, { force: true });
      return response;
    } catch (err) {
      console.error('Error sending chat message:', err);
      throw err;
    }
    finally {
      setIsChatBusy(false);
    }
  }, [journeyStarted, updateSessionId, loadMicroreports, fetchStepStatus, landingRevealAgent]);

  const handleGenerateFraming = useCallback(async (goal: string) => {
    try {
      setLoading(true);
      if (!journeyStarted) setJourneyStarted(true);
      const response = await apiService.generateFramingReport(goal, landingRevealAgent || undefined);
      updateSessionId(response.session_id);
      isNewWorkspaceRef.current = true;
      await loadMicroreports({ sessionId: response.session_id, autoSelectLatest: true }); // ensure latest selection
      await fetchStepStatus(response.session_id, { force: true });
      await fetchReportSections(response.session_id, { force: true });
      await fetchFrameGroups(response.session_id);
      setSessionName('');
      setSessionNameInput('');
      // Check if onboarding will be shown - if not, show session name modal immediately
      const hideOnboarding =
        typeof window !== 'undefined' && window.localStorage?.getItem(WORKSPACE_ONBOARDING_HIDE_KEY) === '1';
      if (hideOnboarding) {
        setShowSessionNameModal(true);
      } else {
        pendingSessionNameModalRef.current = true;
      }
      setWorkspaceAccessRequested(true);
      return response;
    } catch (err) {
      console.error('Error generating framing analysis:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [journeyStarted, updateSessionId, loadMicroreports, fetchStepStatus, fetchReportSections, fetchFrameGroups]);

  // Removed chat events plumbing

  const handleFramesParsed = (frames: InsightFrame[], reportId: string | undefined) => {
    if (!reportId) return;
    if (framesNoticeRef.current !== reportId) {
      framesNoticeRef.current = reportId;
      setSelectedFrameNumbers([]);
    }
  };

  const handleFrameToggle = (_frameNumber: number, frame: InsightFrame) => {
    setSelectedFrameNumbers((prev) => {
      const exists = prev.includes(frame.frameNumber);
      const next = exists ? prev.filter((n) => n !== frame.frameNumber) : [...prev, frame.frameNumber];
      return next.sort((a, b) => a - b);
    });
  };

  const handleClearFrameSelection = () => {
    setSelectedFrameNumbers([]);
  };

  const handleSubmitSelectedFrames = useCallback((frames: InsightFrame[], _autoRun?: boolean) => {
    console.log('[handleSubmitSelectedFrames] Called with frames:', frames.length);
    if (!frames.length) {
      console.log('[handleSubmitSelectedFrames] No frames, returning early');
      return;
    }
    researchAutoStartRequestedRef.current = false;
    setPendingFrameSelection(frames);
    setPendingGenerationMode('with_additional_frames');
    setShowCreditConfirmationModal(true);
  }, []);

  const handleProceedWithFrameSelection = useCallback(async () => {
    if (!sessionId || !pendingGenerationMode) {
      console.log('[handleProceedWithFrameSelection] Missing session or pending mode');
      return;
    }

    try {
      setIsAnalysisGenerationBusy(true);
      setError(null);
      setShowCreditConfirmationModal(false);
      setAugmentationStatusMessage('Queuing up your analysis...');

      if (pendingGenerationMode === 'without_additional_frames') {
        researchAutoStartRequestedRef.current = false;
        await apiService.skipFrameSelection(sessionId);
        setSelectedFrameNumbers([]);
        setPendingFrameSelection(null);
        setPendingGenerationMode(null);
        setMicroreportsVisible(true);
        setJourneyUnlocked(true);
        setAnalysisQueuePending(true);
        setAugmentationStatusMessage('Analysis generation queued. Preparing microreport workers...');
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(queueReloadStorageKey);
        }
        const startResult = await apiService.startResearchWorker(sessionId);
        if (startResult?.status === 'already_complete') {
          setAnalysisQueuePending(false);
          setAugmentationStatusMessage(null);
        } else if (startResult?.status === 'already_running') {
          setError(null);
          setAugmentationStatusMessage(
            'Analysis generation is already running. Syncing updates...'
          );
        } else if (startResult?.status === 'queued') {
          setError(null);
          setAugmentationStatusMessage(
            'Analysis generation queued. Preparing microreport workers...'
          );
        }
        await fetchStepStatus(sessionId, { force: true });
        return;
        //
      }

      const frames = pendingFrameSelection ?? [];
      if (!frames.length) {
        throw new Error('No additional frames selected for generation');
      }

      const frameNumbers = frames.map((frame) => frame.frameNumber).sort((a, b) => a - b);
      console.log('[handleProceedWithFrameSelection] Proceeding with frames:', frameNumbers);
      console.log('[handleProceedWithFrameSelection] Calling apiService.submitFrameSelection...');
      const response = await apiService.submitFrameSelection(frameNumbers, undefined, true);
      console.log('[handleProceedWithFrameSelection] API response:', response);
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          queueReloadStorageKey,
          JSON.stringify({ sessionId: response.session_id, startedAt: Date.now() })
        );
      }
      setAnalysisQueuePending(true);
      updateSessionId(response.session_id);
      await loadMicroreports({ sessionId: response.session_id });
      await fetchStepStatus(response.session_id, { force: true });
      await fetchReportSections(response.session_id, { force: true });
      setPendingFrameSelection(null);
      setPendingGenerationMode(null);
      setMicroreportsVisible(true);
      setJourneyUnlocked(true);
      console.log('[handleProceedWithFrameSelection] Success!');
    } catch (err) {
      console.error('Error generating analyses:', err);
      if (isAbortError(err)) {
        // Request may be cancelled by timeout/navigation while backend continues.
        // Keep UI in a non-error state and continue polling for status changes.
        setError(null);
        setAugmentationStatusMessage('Request timed out in browser, but generation may still be processing. Syncing status...');
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate analyses';
      setAnalysisQueuePending(false);
      setAugmentationStatusMessage(null);
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(queueReloadStorageKey);
      }
      setError(errorMessage);
    } finally {
      setIsAnalysisGenerationBusy(false);
      console.log('[handleProceedWithFrameSelection] Finished');
    }
  }, [pendingFrameSelection, pendingGenerationMode, sessionId, loadMicroreports, fetchStepStatus, fetchReportSections, updateSessionId]);

  const handleCancelFrameSelection = useCallback(() => {
    console.log('[handleCancelFrameSelection] User cancelled frame selection');
    setShowCreditConfirmationModal(false);
    setPendingGenerationMode(null);
  }, []);

  useEffect(() => {
    if (!showCreditConfirmationModal || !pendingGenerationMode || !sessionId) {
      setIsEstimatingGenerationCredits(false);
      setGenerationEstimate(null);
      setGenerationEstimateError(null);
      return;
    }
    let active = true;
    const requestId = generationEstimateRequestIdRef.current + 1;
    generationEstimateRequestIdRef.current = requestId;
    setIsEstimatingGenerationCredits(true);
    setGenerationEstimateError(null);

    const estimateFrameNumbers =
      pendingGenerationMode === 'with_additional_frames'
        ? (pendingFrameSelection ?? []).map((frame) => frame.frameNumber)
        : undefined;

    void apiService
      .getGenerationEstimate(sessionId, estimateFrameNumbers)
      .then((estimate) => {
        if (!active || generationEstimateRequestIdRef.current !== requestId) return;
        setGenerationEstimate(estimate);
      })
      .catch((err) => {
        if (!active || generationEstimateRequestIdRef.current !== requestId) return;
        const message =
          err instanceof Error && err.message.trim()
            ? err.message
            : 'Failed to estimate credits.';
        setGenerationEstimateError(message);
        setGenerationEstimate(null);
      })
      .finally(() => {
        if (!active || generationEstimateRequestIdRef.current !== requestId) return;
        setIsEstimatingGenerationCredits(false);
      });

    return () => {
      active = false;
    };
  }, [showCreditConfirmationModal, pendingGenerationMode, pendingFrameSelection, sessionId]);

  const handleSkipFrameSelection = useCallback(() => {
    researchAutoStartRequestedRef.current = false;
    setPendingFrameSelection([]);
    setPendingGenerationMode('without_additional_frames');
    setShowCreditConfirmationModal(true);
  }, []);

  const handleResumeSession = async (resumeSessionId: string) => {
    try {
      resetLandingProgress();
      setLandingError(null);
      setIsLandingParsing(true);
      updateSessionId(resumeSessionId);
      isNewWorkspaceRef.current = false;
      setJourneyStarted(true);
      // Get session name from availableSessions if available
      const sessionInfo = availableSessions.find(s => s.session_id === resumeSessionId);
      setWorkspaceBriefId(sessionInfo?.brief_id || null);
      const existingName = (sessionInfo?.session_name || '').trim();
      setSessionName(existingName);
      setSessionNameInput(existingName);
      setShowSessionNameModal(shouldShowSessionNameModal(existingName));
      sessionNameModalDismissedForSessionRef.current = shouldShowSessionNameModal(existingName)
        ? null
        : resumeSessionId;
      await Promise.all([
        loadMicroreports({ sessionId: resumeSessionId, autoSelectLatest: true }),
        fetchStepStatus(resumeSessionId, { force: true })
      ]);
      await fetchFrameGroups(resumeSessionId);
      setSelectedFrameNumbers([]);
      setWorkspaceAccessRequested(true);
      setActiveView('workspace');
    } catch (err) {
      console.error('Error resuming session:', err);
      setLandingError('Unable to open that workspace. Please try another one.');
    } finally {
      setIsLandingParsing(false);
    }
  };

  const handleCloseWorkspace = useCallback(async () => {
    if (isClosingWorkspace) return;

    setIsClosingWorkspace(true);
    stopLandingStatusPolling();
    setIsLandingParsing(false);

    try {
      if (sessionId) {
        await apiService.closeSession();
      }
    } catch (err) {
      console.error('Failed to close workspace', err);
      setIsClosingWorkspace(false);
      return;
    }

    updateSessionId(null);
    latestFetchIdRef.current = null;
    pinnedReportIdRef.current = null;
    framesNoticeRef.current = null;
    setWorkspaceAccessRequested(false);
    setMicroreports([]);
    setSelectedReport(null);
    setStepStatuses([]);
    setStepStage(null);
    setFinalRegenerating(false);
    setSelectedFrameNumbers([]);
    setCompletedFrameNumbers([]);
    setMicroreportsVisible(false);
    setJourneyStarted(false);
    setJourneyUnlocked(false);
    setScratchpadItems([]);
    setSessionName('');
    setSessionNameInput('');
    setShowSessionNameModal(false);
    sessionNameModalDismissedForSessionRef.current = null;
    setShowBriefBuilder(false);
    setLandingRevealAgent(null);
    setDraftSessionId(null);
    setWorkspaceBriefId(null);
    setLandingInput('');
    setLandingError(null);
    setActiveView('landing');
    setShouldAutoSelectLatest(true);
    setFrameGroups(null);
    setIsClosingWorkspace(false);
  }, [sessionId, isClosingWorkspace, stopLandingStatusPolling, updateSessionId]);

  const handleLandingSeedSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = landingInput.trim();
      if (!trimmed) {
        setLandingError('Please enter a prompt to begin.');
        return;
      }
      setLandingError(null);
      setLandingInput('');
      setPendingBriefPrompt(trimmed);
      setShowBriefBuilder(true);
    },
    [landingInput]
  );

  useEffect(() => {
    if (!showBriefBuilder) return;
    if (!pendingBriefPrompt) return;
    if (!briefBuilderRef.current) return;
    const prompt = pendingBriefPrompt;
    setPendingBriefPrompt(null);
    void briefBuilderRef.current.sendPrompt(prompt);
  }, [showBriefBuilder, pendingBriefPrompt]);



  const handleLaunchFromBrief = useCallback(async ({ briefId, finalPrompt }: { briefId?: string | null; finalPrompt: string }) => {
    const trimmed = finalPrompt.trim();
    if (!trimmed) {
      setLandingError('Please provide a final brief before launching.');
      throw new Error('Final prompt is empty');
    }

    const previousSessionId = apiService.getSessionId();
    let targetSessionId: string;
    try {
      targetSessionId = await ensureDraftSession();
    } catch (err) {
      console.error('Failed to prepare draft session for launch', err);
      emitLaunchFlowEvent({
        event_name: 'launch_prepare_session_failed',
        session_id: previousSessionId || undefined,
        error: err instanceof Error ? err.message : String(err),
      });
      setLandingError('Unable to start workspace right now. Please try again.');
      throw err instanceof Error ? err : new Error('Failed to prepare session');
    }

    emitLaunchFlowEvent({
      event_name: 'launch_clicked',
      session_id: targetSessionId,
      payload: {
        brief_id_present: Boolean(briefId),
        previous_session_id: previousSessionId,
      },
    });

    setLandingError(null);
    setWorkspaceBriefId(briefId || null);
    resetLandingProgress();
    setIsLandingParsing(true);
    apiService.setSessionId(targetSessionId);
    launchReadinessGateActiveRef.current = true;
    startLandingStatusMonitor(targetSessionId);

    let response: GenerateReportResponse | null = null;
    
    try {
      if (!journeyStarted) setJourneyStarted(true);
      response = briefId
        ? await apiService.launchWorkspaceFromBrief(briefId, trimmed, landingRevealAgent || undefined, targetSessionId)
        : await apiService.generateFramingReport(trimmed, landingRevealAgent || undefined);
      emitLaunchFlowEvent({
        event_name: 'launch_request_acknowledged',
        session_id: response?.session_id || targetSessionId,
        payload: {
          target_session_id: targetSessionId,
          response_session_id: response?.session_id || null,
        },
      });
    } catch (err) {
      console.warn('Initial launch request failed or timed out, continuing to poll for results:', err);
      emitLaunchFlowEvent({
        event_name: 'launch_request_error',
        session_id: targetSessionId,
        error: err instanceof Error ? err.message : String(err),
      });
      // Don't throw - backend may still be processing, continue polling
    }

    // If we got a response, use that session ID. Otherwise, continue with targetSessionId
    const finalSessionId = response?.session_id || targetSessionId;
    updateSessionId(finalSessionId);
    apiService.setSessionId(finalSessionId);
    sessionNameModalDismissedForSessionRef.current = null;
    
    // Poll for completion - wait for true workflow readiness.
    const maxWaitTime = 10 * 60 * 1000; // 10 minutes max
    const pollInterval = 3000; // 3 seconds
    const startTime = Date.now();
    let workflowReady = false;
    let startupReportsReady = false;
    let startupFramesReady = false;
    let launchPollTick = 0;
    let launchPollSignature = '';
    let launchReportsSignature = '';
    let launchFramesSignature = '';
    
    while (Date.now() - startTime < maxWaitTime) {
      launchPollTick += 1;
      try {
        // Best-effort preload for workspace panel; should never block launch transition.
        await loadMicroreports({ sessionId: finalSessionId, autoSelectLatest: true });
      } catch (pollErr) {
        // Ignore preload errors; readiness check below is authoritative.
        console.warn('[launch-debug] preload failed', {
          sessionId: finalSessionId,
          tick: launchPollTick,
          error: pollErr instanceof Error ? pollErr.message : String(pollErr),
        });
        emitLaunchFlowEvent({
          event_name: 'launch_preload_failed',
          session_id: finalSessionId,
          tick: launchPollTick,
          error: pollErr instanceof Error ? pollErr.message : String(pollErr),
        });
      }

      try {
        const launchStatus = await apiService.getLandingStatus(finalSessionId);
        const readyNow = Boolean(launchStatus?.ready);
        const nextSignature = JSON.stringify({
          ready: readyNow,
          progress_percent: launchStatus?.progress_percent ?? null,
          status_text: launchStatus?.status_text ?? null,
          checks: launchStatus?.checks ?? null,
          blockers: launchStatus?.blockers ?? null,
        });
        if (nextSignature !== launchPollSignature) {
          launchPollSignature = nextSignature;
          emitLaunchFlowEvent({
            event_name: 'launch_poll_status',
            session_id: finalSessionId,
            tick: launchPollTick,
            ready: readyNow,
            progress_percent: typeof launchStatus?.progress_percent === 'number' ? launchStatus.progress_percent : undefined,
            status_text: typeof launchStatus?.status_text === 'string' ? launchStatus.status_text : undefined,
            checks: launchStatus?.checks,
            blockers: launchStatus?.blockers,
          });
        }
        if (readyNow && !workflowReady) {
          workflowReady = true;
          landingProgressPhaseRef.current = 'reports';
          logLaunchProgressDebug('progress_phase_transition', {
            phase: 'reports',
            reason: 'landing_status_ready',
            tick: launchPollTick,
            session_id: finalSessionId,
          });
          setLandingProgressTarget(85, {
            statusText: 'Workspace ready. Loading startup analyses…',
          });
          emitLaunchFlowEvent({
            event_name: 'launch_ready_detected',
            session_id: finalSessionId,
            tick: launchPollTick,
          });
        }
      } catch (readyErr) {
        // Ignore transient readiness fetch errors and keep polling.
        console.warn('[launch-debug] ready-check failed', {
          sessionId: finalSessionId,
          tick: launchPollTick,
          error: readyErr instanceof Error ? readyErr.message : String(readyErr),
        });
        emitLaunchFlowEvent({
          event_name: 'launch_ready_check_failed',
          session_id: finalSessionId,
          tick: launchPollTick,
          error: readyErr instanceof Error ? readyErr.message : String(readyErr),
        });
      }

      if (workflowReady) {
        try {
          const reports = await apiService.getSessionReports(finalSessionId);
          const hasFramingReport = reports.some((report) => (report.type || '').toLowerCase() === 'framing');
          const hasQuickTrendsReport = reports.some((report) => (report.type || '').toLowerCase() === 'early_insights');
          const reportsNowReady = hasFramingReport && hasQuickTrendsReport;
          const nextReportsSignature = JSON.stringify({
            hasFramingReport,
            hasQuickTrendsReport,
            reportsCount: reports.length,
          });
          if (nextReportsSignature !== launchReportsSignature) {
            launchReportsSignature = nextReportsSignature;
            emitLaunchFlowEvent({
              event_name: 'launch_reports_status',
              session_id: finalSessionId,
              tick: launchPollTick,
              payload: {
                has_framing_report: hasFramingReport,
                has_quick_trends_report: hasQuickTrendsReport,
                reports_count: reports.length,
              },
            });
          }
          if (reportsNowReady) {
            startupReportsReady = true;
            landingProgressPhaseRef.current = 'frames';
            logLaunchProgressDebug('progress_phase_transition', {
              phase: 'frames',
              reason: 'startup_reports_ready',
              tick: launchPollTick,
              session_id: finalSessionId,
            });
            setLandingProgressTarget(95, {
              statusText: 'Startup analyses ready. Loading frame cards…',
            });
          }
        } catch (reportsErr) {
          // Keep polling; report fetch can be transiently unavailable.
          console.warn('[launch-debug] reports-check failed', {
            sessionId: finalSessionId,
            tick: launchPollTick,
            error: reportsErr instanceof Error ? reportsErr.message : String(reportsErr),
          });
          emitLaunchFlowEvent({
            event_name: 'launch_reports_check_failed',
            session_id: finalSessionId,
            tick: launchPollTick,
            error: reportsErr instanceof Error ? reportsErr.message : String(reportsErr),
          });
        }
      }

      if (workflowReady && startupReportsReady) {
        try {
          const frames = await apiService.getSessionFrames(finalSessionId);
          setFrameGroups(frames);
          startupFramesReady = true;
          landingProgressPhaseRef.current = 'done';
          logLaunchProgressDebug('progress_phase_transition', {
            phase: 'done',
            reason: 'frame_groups_ready',
            tick: launchPollTick,
            session_id: finalSessionId,
          });
          // Keep below 100 until we actually transition into workspace.
          setLandingProgressTarget(99, {
            statusText: 'Finalizing workspace…',
          });
          const nextFramesSignature = JSON.stringify({
            selector_count: Array.isArray(frames?.selector) ? frames.selector.length : 0,
            generator_count: Array.isArray(frames?.generator) ? frames.generator.length : 0,
          });
          if (nextFramesSignature !== launchFramesSignature) {
            launchFramesSignature = nextFramesSignature;
            emitLaunchFlowEvent({
              event_name: 'launch_frames_status',
              session_id: finalSessionId,
              tick: launchPollTick,
              payload: {
                selector_count: Array.isArray(frames?.selector) ? frames.selector.length : 0,
                generator_count: Array.isArray(frames?.generator) ? frames.generator.length : 0,
              },
            });
          }
          break;
        } catch (framesErr) {
          // Keep polling until frame groups are hydrated as part of launch readiness.
          console.warn('[launch-debug] frames-check failed', {
            sessionId: finalSessionId,
            tick: launchPollTick,
            error: framesErr instanceof Error ? framesErr.message : String(framesErr),
          });
          emitLaunchFlowEvent({
            event_name: 'launch_frames_check_failed',
            session_id: finalSessionId,
            tick: launchPollTick,
            error: framesErr instanceof Error ? framesErr.message : String(framesErr),
          });
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    if (!workflowReady || !startupReportsReady || !startupFramesReady) {
      console.error('Timed out waiting for workspace startup requirements');
      emitLaunchFlowEvent({
        event_name: 'launch_timeout',
        session_id: finalSessionId,
        tick: launchPollTick,
        error: !workflowReady
          ? 'Workspace did not become ready before timeout'
          : !startupReportsReady
            ? 'Startup reports were not ready before timeout'
            : 'Frame groups were not ready before timeout',
      });
      setLandingError(
        !workflowReady
          ? 'Workspace is taking longer than expected. Please check your sessions.'
          : !startupReportsReady
            ? 'Workspace is ready, but startup reports are still syncing. Please retry in a moment.'
            : 'Workspace is ready, but frame cards are still syncing. Please retry in a moment.',
      );
      setIsLandingParsing(false);
      resetLandingProgress();
      return;
    }

    try {
      await fetchStepStatus(finalSessionId, { force: true });
      await fetchSessions();
    } catch (err) {
      console.warn('Error fetching step status or sessions:', err);
      // Non-fatal, continue
    }

    landingProgressPhaseRef.current = 'done';
    logLaunchProgressDebug('progress_phase_transition', {
      phase: 'done',
      reason: 'workspace_transition',
      session_id: finalSessionId,
    });
    setLandingProgressTarget(100, { statusText: 'Ready. Opening workspace…' });

    setDraftSessionId(null);
    isNewWorkspaceRef.current = true;
    setSessionName('');
    setSessionNameInput('');
    // Check if onboarding will be shown - if not, show session name modal immediately
    const hideOnboarding =
      typeof window !== 'undefined' && window.localStorage?.getItem(WORKSPACE_ONBOARDING_HIDE_KEY) === '1';
    if (hideOnboarding) {
      setShowSessionNameModal(true);
      sessionNameModalDismissedForSessionRef.current = null;
    } else {
      pendingSessionNameModalRef.current = true;
    }
    setSelectedFrameNumbers([]);
    setShowBriefBuilder(false);
    resetLandingProgress();
    setIsLandingParsing(false);
    setWorkspaceAccessRequested(true);
    setActiveView('workspace');
    emitLaunchFlowEvent({
      event_name: 'launch_transition_workspace',
      session_id: finalSessionId,
      payload: {
        reason: 'backend_ready',
      },
    });
  }, [
    emitLaunchFlowEvent,
    ensureDraftSession,
    fetchSessions,
    fetchStepStatus,
    journeyStarted,
    loadMicroreports,
    logLaunchProgressDebug,
    resetLandingProgress,
    setLandingProgressTarget,
    startLandingStatusMonitor,
    updateSessionId,
  ]);

  // Removed handleLandingFieldChange; landing parsing step removed

  const handleOpenWorkspace = () => {
    resetLandingProgress();
    setIsLandingParsing(false);
    setWorkspaceAccessRequested(true);
    setActiveView('workspace');
  };

  const handleDismissWorkspaceOnboarding = useCallback(() => {
    if (workspaceOnboardingOptOut && typeof window !== 'undefined') {
      window.localStorage?.setItem(WORKSPACE_ONBOARDING_HIDE_KEY, '1');
    }
    setShowWorkspaceOnboarding(false);
    setOnboardingDismissed(true);
    // Show session name modal after onboarding is dismissed
    if (pendingSessionNameModalRef.current) {
      setShowSessionNameModal(true);
      pendingSessionNameModalRef.current = false;
    }
  }, [workspaceOnboardingOptOut]);

  const handleShowWorkspaceOnboarding = useCallback(() => {
    setShowWorkspaceOnboarding(true);
  }, []);

  const handleShowFirstTimeTour = useCallback(() => {
    setShowFirstTimeTour(true);
  }, []);

  const handleShowTourStep = useCallback((step: 'microreports' | 'documentviewer' | 'rightpanel' | 'scratchpad') => {
    setTourStep(step);
  }, []);

  const handleDismissFirstTimeTour = useCallback(() => {
    if (firstTimeTourOptOut && typeof window !== 'undefined') {
      window.localStorage?.setItem(FIRST_TIME_TOUR_HIDE_KEY, '1');
    }
    setShowFirstTimeTour(false);
    // Start the tour by moving to first step
    setTourStep('microreports');
  }, [firstTimeTourOptOut]);

  const handleTourStepComplete = useCallback(() => {
    // Move to next step or complete tour
    if (tourStep === 'microreports') {
      setTourStep('documentviewer');
    } else if (tourStep === 'documentviewer') {
      setTourStep('rightpanel');
    } else if (tourStep === 'rightpanel') {
      setTourStep('scratchpad');
    } else {
      setTourStep(null);
    }
  }, [tourStep]);

  // Dev helper: Create test session and go to workspace
  const handleCreateTestWorkspace = useCallback(async () => {
    try {
      const response = await apiService.initializeSession({ source: 'dev_test' });
      updateSessionId(response.session_id);
      setWorkspaceBriefId(null);
      setJourneyStarted(true);
      setWorkspaceAccessRequested(true);
      setActiveView('workspace');
    } catch (err) {
      console.error('Failed to create test workspace:', err);
      alert('Failed to create test workspace');
    }
  }, [updateSessionId]);

  const isDevSmokeEnabled = useMemo(() => {
    const envName = String(process.env.REACT_APP_ENV_NAME || '').toLowerCase();
    const nodeEnv = String(process.env.NODE_ENV || '').toLowerCase();
    return envName.includes('dev') || nodeEnv === 'development';
  }, []);

  const handleRunDevSmokeTest = useCallback(async () => {
    if (isStartingDevSmokeWorker) return;
    setIsStartingDevSmokeWorker(true);
    setDevSmokeWorkerError(null);
    try {
      const response = await apiService.startDevReportWorkerSmokeTest(sessionId || undefined);
      const initialStatus = response.worker?.status || 'queued';
      setDevSmokeWorkerJob({
        job_id: response.worker.job_id,
        status: initialStatus as DevReportWorkerJobResponse['status'],
        session_id: response.worker.session_id,
        run_id: response.worker.run_id,
        idempotency_key: response.worker.idempotency_key,
        created_at: response.worker.created_at,
        updated_at: response.worker.updated_at,
        started_at: null,
        completed_at: null,
        error_message: null,
        result: {},
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start smoke test';
      setDevSmokeWorkerError(message);
    } finally {
      setIsStartingDevSmokeWorker(false);
    }
  }, [isStartingDevSmokeWorker, sessionId]);

  useEffect(() => {
    const status = devSmokeWorkerJob?.status;
    const jobId = devSmokeWorkerJob?.job_id;
    if (!jobId || !status || !['queued', 'running'].includes(status)) {
      return;
    }
    const interval = window.setInterval(async () => {
      try {
        const next = await apiService.getDevReportWorkerSmokeStatus(jobId);
        setDevSmokeWorkerJob(next);
        if (next.status === 'failed' && next.error_message) {
          setDevSmokeWorkerError(next.error_message);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to poll smoke test';
        setDevSmokeWorkerError(message);
      }
    }, 4000);
    return () => {
      window.clearInterval(interval);
    };
  }, [devSmokeWorkerJob?.job_id, devSmokeWorkerJob?.status]);

  const devSmokeStatusText = useMemo(() => {
    if (devSmokeWorkerError) {
      return `Smoke test error: ${devSmokeWorkerError}`;
    }
    if (!devSmokeWorkerJob) {
      return null;
    }
    const status = devSmokeWorkerJob.status;
    if (status === 'succeeded') {
      return `Smoke test succeeded (job ${devSmokeWorkerJob.job_id}). Check /developer logs for full trace.`;
    }
    if (status === 'failed') {
      return `Smoke test failed (job ${devSmokeWorkerJob.job_id}). ${devSmokeWorkerJob.error_message || ''}`.trim();
    }
    return `Smoke test ${status} (job ${devSmokeWorkerJob.job_id})`;
  }, [devSmokeWorkerError, devSmokeWorkerJob]);

  // Resize handlers for right panel
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const container = layoutRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const rightEdge = rect.right; // px from viewport left
      const handleThickness = 6; // ~w-1.5
      const proposed = rightEdge - e.clientX - handleThickness;
      // clamp between 280 and 900px
      const newWidth = Math.max(280, Math.min(1200, proposed));
      setRightPanelWidth(newWidth);
    };
    const stopResize = () => setIsResizing(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stopResize);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing]);

  // Credit display component
  const CreditDisplay: React.FC = () => {
    return (
      <div 
        className="credit-display"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0 2px',
          color: '#cbd5e1',
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: '0.01em',
          cursor: 'default',
          userSelect: 'none'
        }}
      >
        <Zap size={13} color="#a5b4fc" />
        <span style={{ opacity: 0.8 }}>Credits</span>
        <span style={{ color: '#e0e7ff', fontWeight: 700 }}>{credits.toLocaleString()}</span>
      </div>
    );
  };

  const PrelandingView: React.FC = () => {
    useEffect(() => {
      if (isAuthLoaded && isSignedIn) {
        setActiveView('landing');
      }
    }, [isAuthLoaded, isSignedIn]);

    return (
      <div
        className="astra-prelanding-screen"
        style={{
          backgroundImage: `url(${astraBackground})`,
        }}
      >
        <div className="astra-prelanding-body">
          <div className="astra-prelanding-login">
            {isSignedIn ? (
              <>
                <div className="prelanding-user-controls">
                  <UserProfile />
                  <CreditDisplay />
                </div>
                <div style={{ textAlign: 'center', color: 'white' }}>
                  <p>Welcome back!</p>
                  <p style={{ color: '#94a3b8', marginTop: '8px' }}>
                    You have {credits} credits
                  </p>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <p className="prelanding-signin-copy" style={{ color: '#94a3b8', marginBottom: '32px', marginTop: '120px', fontWeight: 600 }}>
                  Access your suite of deep strategy agents
                </p>
                <div className="prelanding-signin-btn-wrap">
                  <SignInButton />
                </div>
              </div>
            )}
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  };

  // Memoize completedFrames array to prevent unnecessary re-renders
  const memoizedCompletedFrames = useMemo(
    () => completedFrameNumbers,
    [completedFrameNumbers]
  );

  const hasFrameBasedReports = useMemo(() => {
    if (completedFrameNumbers.length > 0) {
      return true;
    }

    const hasPersistedStepOrFinalReport = microreports.some((report) => {
      const reportType = (report.type || '').toLowerCase();
      return reportType === 'step' || reportType === 'final';
    });
    if (hasPersistedStepOrFinalReport) {
      return true;
    }

    // Fallback for edge cases where report rows are still syncing into microreports
    // but step status already reflects persisted output IDs.
    return stepStatuses.some((step) => {
      const status = String(step.status || '').toLowerCase();
      return status === 'completed' || Boolean(step.report_id);
    });
  }, [completedFrameNumbers, microreports, stepStatuses]);
  const presetAnalysesEstimate = useMemo(() => {
    const pendingFromServer = generationEstimate?.pending_microreports;
    if (typeof pendingFromServer === 'number' && pendingFromServer >= 0) {
      return pendingFromServer;
    }
    const reportSectionCount = reportSections?.microreports?.length ?? 0;
    if (reportSectionCount > 0) {
      return reportSectionCount;
    }
    return stepStatuses.length;
  }, [generationEstimate, reportSections, stepStatuses]);

  return (
    <DndProvider backend={HTML5Backend}>
      {/* Auth Header - Shows on all pages except workspace, landing, and prelanding (when signed in) */}
      {activeView !== 'workspace' && activeView !== 'landing' && !(activeView === 'prelanding' && isSignedIn) && (
      <div 
        className="auth-header"
        style={{
          position: 'fixed',
          top: 0,
          right: '60px',
          padding: '16px 24px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}
      >
      {isSignedIn ? (
          <>
            <UserProfile />
            <CreditDisplay />
          </>
        ) : (
          activeView !== 'prelanding' && <SignInButton />
        )}
      </div>
      )}
      {creditSupportError && activeView === 'workspace' && (
        <div
          style={{
            position: 'fixed',
            top: '72px',
            right: '24px',
            maxWidth: '520px',
            padding: '10px 14px',
            borderRadius: '10px',
            border: '1px solid rgba(248, 113, 113, 0.5)',
            backgroundColor: 'rgba(127, 29, 29, 0.85)',
            color: '#fecaca',
            fontSize: '13px',
            lineHeight: 1.35,
            zIndex: 1001,
          }}
        >
          {creditSupportError}
        </div>
      )}
      {sessionAccessError && (
        <div
          style={{
            position: 'fixed',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '640px',
            padding: '12px 18px',
            borderRadius: '10px',
            border: '1px solid rgba(248, 113, 113, 0.5)',
            backgroundColor: 'rgba(127, 29, 29, 0.92)',
            color: '#fecaca',
            fontSize: '13px',
            lineHeight: 1.35,
            zIndex: 1002,
          }}
        >
          {sessionAccessError}
          <button
            type="button"
            onClick={() => {
              setSessionAccessError(null);
              void signOut(() => {
                navigate('/login', { replace: true });
              });
            }}
            style={{
              marginLeft: '12px',
              border: '1px solid rgba(254, 202, 202, 0.45)',
              background: 'transparent',
              color: '#fecaca',
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            Sign in again
          </button>
        </div>
      )}
      <Routes>
        <Route path="/" element={<PrelandingView />} />
        <Route path="/login" element={<PrelandingView />} />
        <Route
          path="/landing"
          element={
            !isAuthLoaded ? null : !isSignedIn ? <Navigate to="/login" replace /> : (
              <LandingView
                landingRevealAgent={landingRevealAgent}
                agentId={landingRevealAgent}
                onRevealAgent={handleRevealLandingAgent}
                showLandingProgress={showLandingProgress}
                showBriefBuilder={showBriefBuilder}
                landingInput={landingInput}
                onLandingInputChange={(value) => setLandingInput(value)}
                onLandingSeedSubmit={handleLandingSeedSubmit}
                isLandingParsing={isLandingParsing}
                landingError={landingError}
                landingProgressSteps={landingProgressSteps}
                landingProgressPercent={landingProgressPercent}
                landingProgressStatusText={landingProgressStatusText}
                onLaunchWorkspaceFromBrief={handleLaunchFromBrief}
                briefBuilderRef={briefBuilderRef}
                draftSessionId={draftSessionId}
                ensureDraftSession={ensureDraftSession}
                fetchSessions={fetchSessions}
                sessionsLoading={sessionsLoading}
                sessionsError={sessionsError}
                availableSessions={filteredSessions}
                deletingSessionId={deletingSessionId}
                onResumeSession={handleResumeSession}
                onDeleteSession={handleDeleteSession}
                onCreateTestWorkspace={handleCreateTestWorkspace}
                showDevSmokeButton={isDevSmokeEnabled}
                devSmokeRunning={isStartingDevSmokeWorker || ['queued', 'running'].includes(devSmokeWorkerJob?.status || '')}
                devSmokeStatusText={devSmokeStatusText}
                onRunDevSmokeTest={handleRunDevSmokeTest}
                onResetLandingPage={handleResetLandingPage}
                isMarketResearch={isMarketResearch}
                agentAccess={agentAccess}
                isSignedIn={isSignedIn}
                profile={profile}
                refreshProfile={refreshProfile}
                CreditDisplay={CreditDisplay}
              />
            )
          }
        />
        <Route 
          path="/workspace" 
          element={
            !isAuthLoaded ? null : !isSignedIn ? <Navigate to="/login" replace /> : (
              <WorkspaceView
                loading={loading}
                microreports={microreports}
                selectedReport={selectedReport}
                error={error}
                isAnalysisGenerationBusy={isAnalysisGenerationBusy}
                isChatBusy={isChatBusy}
                credits={credits}
                isSignedIn={isSignedIn}
                sessionId={sessionId}
                journeyStarted={journeyStarted}
                stepStatuses={stepStatuses}
                stepStage={stepStage}
                finalRegenerating={finalRegenerating}
                journeyUnlocked={journeyUnlocked}
                reportSections={reportSections}
                augmentationWorkerRunning={augmentationWorkerRunning}
                augmentationStatusMessage={augmentationStatusMessage}
                analysisQueuePending={analysisQueuePending}
                customReportsEnabled={customReportsEnabled}
                customReports={customReports}
                selectedFrameNumbers={selectedFrameNumbers}
                completedFrames={memoizedCompletedFrames}
                frameGroups={frameGroups}
                hasFrameBasedReports={hasFrameBasedReports}
                isMarketResearch={isMarketResearch}
                agentId={currentAgentId}
                briefId={workspaceBriefId}
                workspaceName={sessionName}
                scratchpadItems={scratchpadItems}
                categories={categories}
                rightPanelWidth={rightPanelWidth}
                showFirstTimeTour={showFirstTimeTour}
                firstTimeTourOptOut={firstTimeTourOptOut}
                tourStep={tourStep}
                showSessionNameModal={showSessionNameModal}
                sessionNameInput={sessionNameInput}
                savingSessionName={savingSessionName}
                isResizing={isResizing}
                layoutRef={layoutRef}
                onSelectReport={handleSelectReport}
                onAddToScratchpad={handleAddToScratchpad}
                onAddCategory={handleAddCategory}
                onRemoveScratchpadItem={handleRemoveScratchpadItem}
                onChatMessage={handleChatMessage}
                onGenerateFraming={handleGenerateFraming}
                onCanvasCategoriesChange={handleCanvasCategoriesChange}
                onCloseWorkspace={handleCloseWorkspace}
                onCreateUserReport={handleCreateCustomReport}
                onUpdateCustomReport={handleUpdateCustomReport}
                onDeleteCustomReport={handleDeleteCustomReport}
                onToggleFrame={handleFrameToggle}
                onSubmitFrameSelection={handleSubmitSelectedFrames}
                onClearFrameSelection={handleClearFrameSelection}
                onSkipFrameSelection={handleSkipFrameSelection}
                onFramesParsed={handleFramesParsed}
                onSaveSessionName={handleSaveSessionName}
                onSetSessionNameInput={setSessionNameInput}
                onSetIsResizing={setIsResizing}
                onDismissFirstTimeTour={handleDismissFirstTimeTour}
                onSetFirstTimeTourOptOut={setFirstTimeTourOptOut}
                onShowFirstTimeTour={handleShowFirstTimeTour}
                onTourStepComplete={handleTourStepComplete}
                onShowTourStep={handleShowTourStep}
                showCreditConfirmationModal={showCreditConfirmationModal}
                pendingFrameSelection={pendingFrameSelection}
                pendingGenerationMode={pendingGenerationMode}
                presetAnalysesEstimate={presetAnalysesEstimate}
                generationEstimateStatus={generationEstimate?.status ?? null}
                generationEstimateMessage={generationEstimate?.message ?? null}
                requiredCredits={generationEstimate?.required_credits ?? null}
                isEstimatingGenerationCredits={isEstimatingGenerationCredits}
                generationEstimateError={generationEstimateError}
                onProceedWithFrameSelection={handleProceedWithFrameSelection}
                onCancelFrameSelection={handleCancelFrameSelection}
                isClosingWorkspace={isClosingWorkspace}
                onKillGeneration={handleKillGeneration}
                isKillingGeneration={isKillingGeneration}
              />
            )
          } 
        />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/developer" element={<DeveloperLogView />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {isClosingWorkspace && (
        <>
          <div className="workspace-close-modal-overlay" />
          <div className="workspace-close-modal" role="dialog" aria-modal="true" aria-labelledby="workspace-close-title">
            <div className="workspace-close-modal__panel">
              <Loader2 className="w-10 h-10 text-cyan-300 ca-spin mb-5" />
              <h3 id="workspace-close-title">Saving workspace and closing...</h3>
              <p>Please wait while we save your work.</p>
            </div>
          </div>
        </>
      )}
    </DndProvider>
  );
}

export default App;
